// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { type ReadonlyUint8Array } from "@utils/arrayUtils";
export type Account = {
  homePage: string;
  name: string;
};

export type ActionableActionbarProps = {
  actionableName: string;
  actionableSet: ActionableSetEntry[];
  bookmarked: boolean;
  canAccessCourse: boolean;
  contentMetadata: ContentMetadata_ACTIONABLE;
  freeActionableActive: boolean;
  language: Language;
  showCertificate: boolean;
  userCustomerId: number | null;
  userPortalCanSubscribe: boolean;
  userPortalCorporateId: number | null;
  userPortalNeedsPrivacyPolicy: boolean;
  userPortalSmb: boolean;
  userPortalUsername: string | null;
};

export type ActionableActivityReport = {
  actionable: ContentMetadata_ACTIONABLE;
  customer: CustomerReportView | null;
  timeStamp: DateTime<true>;
};

export type ActionableAdminSearchForm = {
  corporateId: number | null;
  includeInactive: boolean;
  languages: Language[];
  query: string;
};

export type ActionableBulkLoadForm = {
  actionableIds: bigint[];
  corporateId: number | null;
  includeInactive: boolean;
};

export type ActionableEditForm = {
  active: boolean;
  channelIds: bigint[];
  coverFile: FormFile | null;
  ctaVideoUrl: string | null;
  dataIds: number[];
  description: string;
  language: Language;
  linkToActionableId: bigint | null;
  overwriteCourse: boolean;
  peerInsightFeedbackForm: ActionableEditFormPeerFeedbackAnswer[];
  proTalkAboutText: string | null;
  proTalkDateTime: DateTime<true> | null;
  proTalkExpertName: string | null;
  proTalkExpertPortraitFile: FormFile | null;
  proTalkExpertSkills: string[];
  proTalkTitle: string | null;
  proTalkUrl: string | null;
  proTalkVideoUrl: string | null;
  suggestedAnswers: SuggestedAnswerView[];
  title: string;
  unlinkActionable: boolean;
  xapiCourse: FormFile | null;
};

export type ActionableEditFormPeerFeedbackAnswer = {
  answer: string;
  question: PeerInsightQuestionView;
};

export type ActionableEditResult = { kind: "SUCCESS"; data: ActionableEditView } | { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons };

export type ActionableEditView = {
  actionableId: bigint;
  actionableSet: ActionableSetEntry[];
  active: boolean;
  channels: ChannelMetadata[];
  courseUploaded: boolean;
  coverUri: string;
  ctaVideoUrl: string | null;
  description: string;
  language: Language;
  name: string;
  peerFeedbackDashboardUnlocked: boolean;
  peerFeedbackDummyQuestions: PeerInsightQuestionView[];
  proTalkAboutText: string | null;
  proTalkDateTime: DateTime<true> | null;
  proTalkExpertName: string | null;
  proTalkExpertPortraitUri: string | null;
  proTalkExpertSkills: string[];
  proTalkTitle: string | null;
  proTalkUrl: string | null;
  proTalkVideoUrl: string | null;
  suggestedAnswerQuestions: PeerInsightQuestionView[];
  suggestedAnswers: SuggestedAnswerView[];
  summaries: ContentMetadata_SUMMARY[];
  title: string;
};

export type ActionableFiltersForm = {
  languageFormFilter: Language[];
};

export type ContentId_ACTIONABLE = {
  contentType: "ACTIONABLE";
  id: bigint;
};

export type ActionableIdsList = {
  actionableIds: bigint[];
};

export type ActionableListPortletEditView = {
  actionables: ContentMetadata_ACTIONABLE[];
  portlet: PortletEditView;
};

export type ActionableListPortletView = {
  items: ContentMetadata_ACTIONABLE[];
  metadata: PortletMetadata;
  pageInfo: PageInfo;
  viewAllUri: string | null;
};

export type ContentMetadata_ACTIONABLE = {
  actionableId: bigint;
  activatedAt: DateTime<true> | null;
  active: boolean;
  common: ContentMetadataCommon;
  contentId: ContentId_ACTIONABLE;
  contentType: "ACTIONABLE";
  coverUri: string;
  description: string;
  language: Language;
  modifiedAt: DateTime<true>;
  name: string;
  tags: Tag[];
  title: string;
};

export type ActionableMetadataArray = {
  items: ContentMetadata_ACTIONABLE[];
};

export type ActionableMiniView = {
  actionableId: bigint;
  coverUri: string;
  name: string;
  title: string;
};

export type ActionableReflectSectionProps = {
  actionableId: bigint;
  freeActionableActive: boolean;
  isMapp: boolean;
  language: Language;
};

export type ActionableSetEntry = {
  actionableId: bigint;
  active: boolean;
  language: "en" | "de" | "es" | "ru" | "zh" | "pt" | "fr";
  name: string;
  title: string;
};

export type Activity = {
  definition: ActivityDefinition;
  id: string;
  objectType: string;
};

export type ActivityDefinition = {
  description: { additionalProperties: Record<string, string> };
  moreInfo: string;
  name: { additionalProperties: Record<string, string> };
  type: string;
};

export type Actor = {
  account: Account | null;
  mbox: string | null;
  name: string;
  objectType: string;
  openid: string | null;
};

export type Address = {
  address1: string;
  address2: string | null;
  country: string;
  fullname: string;
  state: string | null;
  town: string;
  zip: string;
};

export type AdminSidebarWidgetProps = {
  navigationItems: AdminToolLinkGroup[];
};

export type AdminToolLink = {
  description: string | null;
  hidden: boolean;
  ico: string | null;
  linkText: string | null;
  tabName: string | null;
  tabTitle: string | null;
  targetType: AdminToolLinkTargetType;
  url: string | null;
};

export type AdminToolLinkGroup = {
  ico: string | null;
  links: AdminToolLink[];
  name: string | null;
};

export namespace AdminToolLinkTargetType {
  const values = ["SELF", "BLANK", "ROUTER"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AdminToolLinkTargetType = AdminToolLinkTargetType.Impl;

export type AlipayPaymentView = {
  outTradeNo: string | null;
  tradeNo: string | null;
  transactionId: number;
};

export type AllAbstractsListWrapper = {
  activatedAt: { year: number; month: number; day: number } | null;
  audio: boolean;
  audioActivatedAt: { year: number; month: number; day: number } | null;
  dataId: number;
  deactivatedAt: { year: number; month: number; day: number } | null;
  enteredAt: { year: number; month: number; day: number } | null;
  epub: boolean;
  imprint: string;
  language: "en" | "de" | "es" | "ru" | "zh" | "pt" | "fr";
  managingEditor: string;
  modifiedAt: { year: number; month: number; day: number } | null;
  pdf: boolean;
  publisher: string;
  summaryDocumentType: string;
  summarySourceType: "BOOK" | "VIDEO" | "ARTICLE" | "CHAPTER" | "REPORT" | "PODCAST";
  title: string;
};

export type AllAbstractsListWrapperArray = {
  allAbstractsListWrapper: AllAbstractsListWrapper[];
};

export type AndroidPaymentView = {
  appStoreProductId: string | null;
  autoRenewing: boolean;
  countryCode: string | null;
  orderId: string | null;
  paymentState: number;
  purchaseState: number;
  testPurchase: boolean;
  transactionId: number;
};

export type ApiErrorResponse = {
  errorCode: string | null;
  errorMessage: string | null;
};

export namespace ApprovalStatus {
  const values = ["APPROVED", "REJECTED", "IGNORED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ApprovalStatus = ApprovalStatus.Impl;

export namespace AreaOfExpertise {
  const values = [
    "ACCOUNTING_AND_FINANCE",
    "ADMINISTRATION",
    "BUSINESS_DEVELOPMENT",
    "COMMUNICATIONS",
    "CUSTOMER_SERVICE",
    "HUMAN_RESOURCES",
    "INFORMATION_TECHNOLOGY",
    "LEGAL",
    "MARKETING",
    "OPERATIONS",
    "PRODUCTION",
    "SALES",
    "SUPPLY_MANAGEMENT",
    "OTHER",
    "UNDEFINED",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AreaOfExpertise = AreaOfExpertise.Impl;

export namespace AskGetabstractAnalyticsEventVariant {
  const values = ["default", "qa_triggered_from_search", "qa_demo"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AskGetabstractAnalyticsEventVariant = AskGetabstractAnalyticsEventVariant.Impl;

export type AskGetabstractAnalyticsFeedbackEventForm = {
  isPositive: boolean;
  questionUuid: string;
};

export type AskGetabstractAnswer = {
  answer: string;
  contextDocs: AskGetabstractAnswerContextDoc[];
  detectedLanguageLabel: string | null;
  previousQuestionUuid: string | null;
  question: string;
  questionUuid: string;
  relatedActionables: ActionableMiniView[];
  relatedQuestions: string[];
  status: AskGetabstractAnswerStatus;
};

export type AskGetabstractAnswerContextDoc = {
  bookmarked: boolean;
  dataId: number;
  rating: number | null;
  referenceNumber: number;
  snippet: string;
  sourceTypeString: string;
  title: string;
  urls: {
    cover: string;
    list: string;
    reference: string;
  };
};

export namespace AskGetabstractAnswerStatus {
  const values = ["PROCESSING", "FINISHED", "NO_DOCS_FROM_VECTOR_DB", "NO_DOCS_AFTER_FILTER", "NO_REFERENCE_IN_ANSWER", "GENERAL_ERROR", "TIMEOUT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AskGetabstractAnswerStatus = AskGetabstractAnswerStatus.Impl;

export type AskGetabstractPortletView = {
  items: string[];
  metadata: PortletMetadata;
};

export type AskGetabstractProperties = {
  isTrendingQuestion: boolean;
  language: Language;
  mappWebView: boolean;
  question: string | null;
  questionUuid: string | null;
  showAiDataProtectionDisclaimer: boolean;
};

export type AskGetabstractQuestion = {
  analyticsEventVariant: AskGetabstractAnalyticsEventVariant;
  blacklistQuestions: string[];
  previousQuestionUuid: string | null;
  question: string;
  questionUuid: string;
  relatedQuestionStep: number;
  trendingQuestion: boolean;
};

export type AskGetabstractQuestionAnswers = {
  current: AskGetabstractAnswer;
  history: AskGetabstractAnswer[];
};

export namespace AuthenticationType {
  const values = ["BASIC", "OAUTH2", "JWT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AuthenticationType = AuthenticationType.Impl;

export type AuthorAdminListFilters = {
  authorId: string | null;
  channels: string | null;
  firstName: string | null;
  gender: string | null;
  lastName: string | null;
  middleName: string | null;
};

export type AuthorAdminListView = {
  authorId: bigint;
  channels: string[];
  firstName: string;
  gender: Gender;
  lastName: string | null;
  middleName: string | null;
};

export type AuthorEditView = {
  authorId: bigint | null;
  firstName: string;
  gender: Gender;
  ignoringWarning: boolean;
  lastName: string | null;
  middleName: string | null;
};

export type AuthorListView = {
  authorId: number;
  firstName: string | null;
  gender: Gender | null;
  lastName: string | null;
  middleName: string | null;
};

export type B2BStatsReportPageProps = {
  b2bReportForm: NewB2BReportForm;
  corporateId: number;
  hasExtendedAccess: boolean;
  reportTypes: DropdownEntry[];
};

export type B2BStatsReportSaveResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; reportId: number };

export type B2CRenewalEmails = {
  name: string;
  sendDate: DateTime<true>;
  type: string;
};

export type B2CSubscription = {
  actionStatus: string | null;
  customerId: number;
  editSubscriptionUrl: string;
  endDate: { year: number; month: number; day: number };
  giftSubscription: GiftSubscription | null;
  isCredited: boolean;
  isExpired: boolean;
  isLoss: boolean;
  isMobileSubscription: boolean;
  isPaid: boolean;
  isTeamSubscription: boolean;
  manualRenewalUrl: string;
  numberOfTransactions: number;
  product: string;
  productId: number;
  quantity: number;
  randomKey: string | null;
  startDate: { year: number; month: number; day: number };
  studentVerification: StudentVerificationView | null;
  subscriptionId: number;
  subscriptionPayment: SubscriptionPayment;
  subscriptionStatus: SubscriptionStatus;
  teamSubscription: TeamSubscription | null;
};

export type BankConnectionDetails = {
  abaNumber: string | null;
  accountNumber: string | null;
  bankConnectionType: "IBAN" | "ABA" | "SWIFTBIC" | "CHEQUE";
  bankname: string;
  iban: string | null;
  swiftBic: string | null;
};

export type BookmarkResponse = {
  bookmarked: boolean;
};

export type BytesData = {
  data: ReadonlyUint8Array;
};

export type CareerEditView = {
  careerId: bigint;
  form: CareerForm;
};

export type CareerForm = {
  active: boolean;
  careerLocalization: { additionalProperties: Record<string, CareerLocalizationForm> };
};

export namespace CareerLevel {
  const values = [
    "OWNER_EXECUTIVE_C_LEVEL",
    "SENIOR_MANAGEMENT",
    "MIDDLE_MANAGEMENT",
    "JUNIOR_MANAGEMENT",
    "ADMINISTRATIVE_STAFF",
    "EMPLOYEE_WITHOUT_MANAGEMENT_OR_LEADERSHIP_RESPONSIBILITY",
    "STUDENT_OR_TRAINEE",
    "TEACHER_OR_ACADEMIC",
    "NOT_EMPLOYED",
    "RETIRED",
    "OTHER",
    "UNDEFINED",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CareerLevel = CareerLevel.Impl;

export type CareerListView = {
  active: boolean;
  careerId: bigint;
  languages: string[];
  location: string;
  title: string;
};

export type CareerListViewList = {
  items: CareerListView[];
};

export type CareerLocalizationForm = {
  benefits: string;
  content: string;
  location: string;
  title: string;
};

export type CareerView = {
  active: boolean;
  benefits: string;
  careerId: bigint;
  content: string;
  location: string;
  title: string;
};

export type ChannelAdminSearchForm = {
  corporateId: number | null;
  includeInactive: boolean | null;
  includeTemporary: boolean | null;
  query: string;
};

export type ChannelAdminTagBasedProps = {
  channelId: bigint;
  managerSelection: ChannelManager[];
};

export type ChannelCoverEditForm = { kind: "default" } | { kind: "update"; base64Image: string } | { kind: "existing"; url: string };

export type ChannelEditForm = {
  active: boolean;
  channelId: bigint;
  channelLocalization: { additionalProperties: Record<string, ChannelLocalizationEditForm> };
  childChannels: ChannelRelationForm[];
  cover: ChannelCoverEditForm;
  definition: string | null;
  name: string;
  notes: string | null;
  primarySortOrder: number | null;
  tag: Tag;
};

export type ChannelEditView = {
  active: boolean;
  channelId: bigint;
  channelLocalization: { additionalProperties: Record<string, ChannelLocalizationView> };
  childChannels: ChannelRelationView[];
  cover: string | null;
  definition: string | null;
  name: string;
  notes: string | null;
  primarySortOrder: number | null;
  tag: Tag;
};

export type ChannelIdsList = {
  channelIds: bigint[];
};

export type ChannelInsightListView = {
  channelId: bigint;
  summaryCount: number;
  title: string | null;
  topSummaries: ContentMetadata_SUMMARY[];
};

export type ChannelList = {
  channels: ChannelMetadata[];
};

export type ChannelLocalizationEditForm = {
  active: boolean;
  description: string | null;
  manager: number | null;
  synonym: string | null;
  title: string | null;
};

export type ChannelLocalizationView = {
  active: boolean;
  description: string | null;
  manager: number | null;
  synonym: string | null;
  title: string | null;
};

export type ChannelManager = {
  id: number;
  name: string;
};

export type ChannelMetadata = {
  activationTime: DateTime<true> | null;
  active: boolean;
  channelId: bigint;
  channelLocalization: { additionalProperties: Record<string, ChannelTextLocalization> };
  coverUri: string | null;
  definition: string | null;
  descriptionHtml: string | null;
  modificationTime: DateTime<true> | null;
  name: string;
  synonyms: string | null;
  tag: Tag;
  title: string | null;
  visibleLanguages: Language[];
};

export type ChannelMetadataArray = {
  channelMetadata: ChannelMetadata[];
};

export type ChannelRelationForm = {
  channelId: bigint;
  sortOrder: number | null;
  title: string;
};

export type ChannelRelationView = {
  channelId: bigint;
  sortOrder: number | null;
  title: string;
};

export type ChannelSaveResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; channelId: bigint };

export type ChannelTextLocalization = {
  description: string | null;
  synonym: string | null;
  title: string | null;
};

export type ConnectorCompletionExecutionLogListView = {
  completedAt: DateTime<true> | null;
  connectorExecutionLogId: bigint;
  contentId: string;
  method: string;
  request: string;
  response: string;
  statusCode: number;
  syncedAt: DateTime<true>;
  userId: string;
};

export type ConnectorCompletionFilters = {
  timeFrame: string;
  userId: string;
};

export namespace ConnectorCompletionTimeFrame {
  const values = ["LAST_24_HOURS", "LAST_WEEK", "LAST_2_WEEKS", "LAST_MONTH", "LAST_3_MONTHS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorCompletionTimeFrame = ConnectorCompletionTimeFrame.Impl;

export type ConnectorContentExecutionListView = {
  connectorExecutionId: bigint;
  connectorName: string;
  description: string;
  detail: ConnectorContentExecutionLogDetailView;
  provider: ConnectorProviderMetadata;
  syncedAt: DateTime<true> | null;
  type: "WEB" | "BATCH";
};

export type ConnectorContentExecutionLogDetailView = {
  failedExportedItems: number;
  successfullyExportedItems: number;
};

export type ConnectorContentExecutionLogListView = {
  connectorExecutionLogId: bigint;
  contentId: string;
  method: string;
  request: string;
  response: string;
  statusCode: number;
  syncedAt: DateTime<true>;
};

export type ConnectorExecutionLogMetadata = {
  connectorExecutionLogId: bigint;
  contentId: string;
  method: string;
  request: string;
  response: string;
  statusCode: number;
  syncedAt: DateTime<true>;
};

export namespace ConnectorExecutionType {
  const values = ["WEB", "BATCH"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorExecutionType = ConnectorExecutionType.Impl;

export type ConnectorExecutionView = {
  connectorExecutionId: bigint;
  executedAt: DateTime<true>;
  executionType: ConnectorExecutionType;
  logsCount: number;
  provider: ConnectorProviderMetadata;
};

export type ConnectorForm = {
  active: boolean;
  languages: Language[];
  name: string;
  products: ConnectorProduct[];
  settingsForm: ConnectorSettingsForm;
  syncCompletions: boolean;
  syncContent: boolean;
  type: ConnectorType;
};

export namespace ConnectorGrantType {
  const values = ["CLIENT_CREDENTIALS", "PASSWORD"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorGrantType = ConnectorGrantType.Impl;

export type ConnectorListView = {
  active: boolean;
  connectorId: bigint;
  name: string;
  provider: ConnectorProviderMetadata;
  syncedAt: DateTime<true> | null;
};

export namespace ConnectorProduct {
  const values = ["BUSINESS_BOOKS", "ARTICLES_AND_REPORTS", "VIDEOS_AND_PODCASTS", "ACTIONABLES", "VERIFY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorProduct = ConnectorProduct.Impl;

export type ConnectorProviderMetadata = {
  imageUri: string;
  name: string;
  type: ConnectorType;
};

export type ConnectorRealTimeCompletionInfo = {
  completedAt: DateTime<true>;
  contentId: string;
  corporateId: number;
  customerId: number;
  duration: bigint;
  progress: number;
};

export type ConnectorSettingsForm = {
  accountId: string | null;
  apiKey: string | null;
  authenticationType: AuthenticationType;
  certificate: string | null;
  clientId: string;
  clientSecret: string;
  doceboCatalogCode: string | null;
  doceboCategoryCode: string | null;
  doceboChannelId: bigint | null;
  grantType: ("CLIENT_CREDENTIALS" | "PASSWORD") | null;
  lmsApiUri: string | null;
  lmsBaseUri: string;
  lmsTokenUri: string | null;
  password: string | null;
  privateKey: string | null;
  scope: string | null;
  sfCompanyId: string | null;
  userId: string | null;
  userIdentifier: ConnectorUserIdentifier;
  username: string | null;
  workdayCategoryFallback: string | null;
  xapiActorId: XApiActorId;
};

export type ConnectorSettingsView = {
  accountId: string | null;
  apiKey: string | null;
  authenticationType: AuthenticationType;
  certificate: string | null;
  clientId: string;
  clientSecret: string;
  doceboCatalogCode: string | null;
  doceboCategoryCode: string | null;
  doceboChannelId: bigint | null;
  grantType: ConnectorGrantType;
  lmsApiUri: string | null;
  lmsBaseUri: string;
  lmsTokenUri: string | null;
  password: string | null;
  privateKey: string | null;
  scope: string | null;
  sfCompanyId: string | null;
  userId: string | null;
  userIdentifier: ConnectorUserIdentifier;
  username: string | null;
  workdayCategoryFallback: string | null;
  xapiActorId: XApiActorId;
};

export type ConnectorSyncEntry = {
  connectorId: bigint;
  corporateId: number;
  type: ConnectorType;
};

export type ConnectorSyncForm = {
  actionableIds: bigint[];
  dataIds: number[];
};

export namespace ConnectorType {
  const values = ["SUCCESS_FACTORS", "DEGREED", "DOCEBO", "DOCEBO_CATALOG", "CSOD", "SUM_TOTAL", "EDCAST", "WORKDAY", "VIVA_LEARNING", "MS_GRAPH", "ORACLE", "LRS_XAPI"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorType = ConnectorType.Impl;

export namespace ConnectorUserIdentifier {
  const values = ["NAME_ID", "EMAIL", "EXTRA_FIELD_1", "EXTRA_FIELD_2", "EXTRA_FIELD_3"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorUserIdentifier = ConnectorUserIdentifier.Impl;

export type ConnectorView = {
  connectorId: bigint;
  languages: Language[];
  name: string;
  products: ConnectorProduct[];
  settings: ConnectorSettingsView;
  syncCompletions: boolean;
  syncContent: boolean;
  syncStartedCompletions: boolean;
  type: ConnectorType;
};

export type ContentCardDemoProps = {
  actionables: ContentMetadata[];
  miabs: ContentMetadata[];
  sketchnotes: ContentMetadata[];
  summaries: ContentMetadata[];
};

export type ContentId =
  | { contentType: "SUMMARY"; id: bigint }
  | { contentType: "ACTIONABLE"; id: bigint }
  | { contentType: "SKETCHNOTE"; id: bigint }
  | { contentType: "MIAB"; id: bigint }
  | { contentType: "EXPERT_VIDEO"; id: bigint }
  | { contentType: "VERIFY"; id: bigint };

export type ContentIdList = {
  items: ContentId[];
};

export namespace ContentItemType {
  const values = ["UNKNOWN", "SUMMARY", "CATEGORY", "CHANNEL", "LGXP", "ACTIONABLE", "MYLIST", "CUSTOMPAGE", "MIAB", "EXPERT_VIDEO", "CUSTOMER_STORY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContentItemType = ContentItemType.Impl;

export type ContentIxStatus =
  | { contentType: "SUMMARY"; bookmarkedAt: DateTime<true> | null; consumedAt: DateTime<true> | null; contentId: ContentId_SUMMARY; customerId: number; likedAt: DateTime<true> | null; readingProgress: number }
  | { contentType: "MIAB"; bookmarkedAt: DateTime<true> | null; contentId: ContentId_MIAB; customerId: number; feedback: boolean | null; feedbackAt: DateTime<true> | null; modifiedAt: DateTime<true> }
  | { contentType: "ACTIONABLE"; bookmarkedAt: DateTime<true> | null; contentId: ContentId_ACTIONABLE; customerId: number }
  | { contentType: "EXPERT_VIDEO"; bookmarkedAt: DateTime<true> | null; contentId: ContentId_EXPERT_VIDEO; customerId: number; feedback: boolean | null; feedbackAt: DateTime<true> | null; modifiedAt: DateTime<true> };

export type ContentIxStatus_ACTIONABLE = {
  bookmarkedAt: DateTime<true> | null;
  contentId: ContentId_ACTIONABLE;
  contentType: "ACTIONABLE";
  customerId: number;
};

export type ContentIxStatus_EXPERT_VIDEO = {
  bookmarkedAt: DateTime<true> | null;
  contentId: ContentId_EXPERT_VIDEO;
  contentType: "EXPERT_VIDEO";
  customerId: number;
  feedback: boolean | null;
  feedbackAt: DateTime<true> | null;
  modifiedAt: DateTime<true>;
};

export type ContentIxStatusList = {
  items: ContentIxStatus[];
};

export type ContentIxStatus_MIAB = {
  bookmarkedAt: DateTime<true> | null;
  contentId: ContentId_MIAB;
  contentType: "MIAB";
  customerId: number;
  feedback: boolean | null;
  feedbackAt: DateTime<true> | null;
  modifiedAt: DateTime<true>;
};

export type ContentIxStatus_SUMMARY = {
  bookmarkedAt: DateTime<true> | null;
  consumedAt: DateTime<true> | null;
  contentId: ContentId_SUMMARY;
  contentType: "SUMMARY";
  customerId: number;
  likedAt: DateTime<true> | null;
  readingProgress: number;
};

export namespace ContentLibrary {
  const values = ["BUSINESS_BOOKS", "CLASSICS_BOOKS", "ARTICLES_AND_REPORTS", "VIDEOS_AND_PODCASTS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContentLibrary = ContentLibrary.Impl;

export type ContentMetadata =
  | {
      contentType: "SUMMARY";
      activatedAt: DateTime<true> | null;
      activationTime: DateTime<true> | null;
      active: boolean;
      audioActivatedAt: DateTime<true> | null;
      audioActive: boolean;
      audioDuration: number;
      authorInfo: string | null;
      authors: AuthorListView[];
      backgroundColor: string | null;
      bestseller: boolean;
      businessClassic: boolean;
      canonicalName: string;
      common: ContentMetadataCommon;
      contentId: ContentId_SUMMARY;
      contentItemId: bigint;
      contentItemType: ContentItemType;
      countriesExcluded: Country[];
      countriesIncluded: Country[];
      coverModifiedAt: DateTime<true> | null;
      coverUri: string;
      dataId: number;
      descriptionHtml: string | null;
      documentType: SummaryDocumentType;
      duration: number;
      imageBorder: boolean;
      language: Language;
      miabId: bigint | null;
      modifiedAt: DateTime<true> | null;
      originalTitle: string | null;
      previewAvailable: boolean;
      publicationDate: string | null;
      publisherInfo: string | null;
      publisherName: string | null;
      rating: number | null;
      sourceType: SummarySourceType;
      subtitle: string | null;
      summaryPages: number;
      summaryWords: number;
      tags: Tag[];
      teaser: string | null;
      title: string | null;
    }
  | {
      contentType: "ACTIONABLE";
      actionableId: bigint;
      activatedAt: DateTime<true> | null;
      active: boolean;
      common: ContentMetadataCommon;
      contentId: ContentId_ACTIONABLE;
      coverUri: string;
      description: string;
      language: Language;
      modifiedAt: DateTime<true>;
      name: string;
      tags: Tag[];
      title: string;
    }
  | { contentType: "SKETCHNOTE"; common: ContentMetadataCommon; contentId: ContentId_SKETCHNOTE }
  | { contentType: "MIAB"; common: ContentMetadataCommon; contentId: ContentId_MIAB; customerId: number | null; dataId: number }
  | {
      contentType: "VERIFY";
      authors: string[];
      common: ContentMetadataCommon;
      content: string;
      contentId: ContentId_VERIFY;
      formatType: string;
      jobProfiles: string;
      keywords: string;
      modifiedAt: DateTime<true> | null;
      publicationDate: string;
      readingTime: string;
      resourceUrl: string;
      source: string;
      subtitle: string;
      summary: string;
      synopsis: string;
      takeaways: string;
    }
  | { contentType: "EXPERT_VIDEO"; common: ContentMetadataCommon; contentId: ContentId_EXPERT_VIDEO };

export type ContentMetadataCommon = {
  activatedAt: DateTime<true> | null;
  audioActive: boolean;
  coverUri: string;
  description: string | null;
  inactive: boolean;
  language: Language;
  leadIn: string | null;
  link: string | null;
  title: string;
};

export type ContentMetadata_EXPERT_VIDEO = {
  common: ContentMetadataCommon;
  contentId: ContentId_EXPERT_VIDEO;
  contentType: "EXPERT_VIDEO";
};

export type ContentMetadataFilterForm = {
  actionable: {
    filter: ActionableFiltersForm;
    pageInfo: PageInfo;
  };
  miab: {
    filter: MiabFiltersForm;
    pageInfo: PageInfo;
  };
  summary: {
    filter: SummaryFiltersForm;
    pageInfo: PageInfo;
    sorting: SummarySort;
  };
  tag: Tag;
};

export type ContentMetadataView = {
  actionable: {
    filter: FormFilter[];
    items: ContentMetadata_ACTIONABLE[];
    pageInfo: PageInfo;
  };
  miab: {
    filter: FormFilter[];
    items: ContentMetadata_MIAB[];
    pageInfo: PageInfo;
  };
  summary: {
    filter: FormFilter[];
    items: ContentMetadata_SUMMARY[];
    pageInfo: PageInfo;
    sorting: FormSorting;
  };
};

export namespace ContextFlag {
  const values = ["BETA", "LOGGED_IN", "STRICT_FREE_TEXT_PRIVACY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContextFlag = ContextFlag.Impl;

export type ContextFlagArr = {
  items: ContextFlag[];
};

export type CorporateLicences = {
  consumedLicences: number;
  limitedLicences: boolean;
  numberLicences: number;
  unidentifiedUsers: number;
};

export type CorporationLecacyForm = {
  info: string;
};

export type CorporationPortalAdminView = {
  active: boolean;
  corporateId: number;
  corporation: string;
  lcResponsible: CustomerView | null;
  logoUrl: string | null;
  portalUrl: string;
  signupUrl: string | null;
  username: string;
};

export type CorporationSamlLogListView = {
  authenticationRequest: string;
  corporationSamlLogId: number;
  exception: string;
  loggedAt: DateTime<true>;
  relayState: string;
  samlResponse: string;
  success: boolean;
};

export type CorporationSamlLogListViewArray = {
  arr: CorporationSamlLogListView[];
};

export type Countries = {
  items: Country[];
};

export type Country = {
  countryCode: string;
  currency: "CHF" | "CNY" | "USD" | "EUR" | "GBP" | "INR" | "JPY";
  names: {
    de: string;
    en: string;
    es: string;
    fr: string;
    pt: string;
    ru: string;
    zh: string;
  };
  phonePrefix: string;
  region: "SOUTHAMERICA" | "EUROPE" | "OCEANIA" | "NORTHAMERICA" | "AFRICA" | "ANTARCTICA" | "ASIA";
};

export type CountryRestrictedTag = {
  countryCodes: string | null;
  tag: Tag;
};

export type CountryRestrictedTagArray = {
  arr: CountryRestrictedTag[];
};

export namespace CountryRestrictionType {
  const values = ["INCLUDED", "EXCLUDED", "NO_RESTRICTION"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CountryRestrictionType = CountryRestrictionType.Impl;

export type CreditCardPaymentView = {
  acqAuthorizationCode: string | null;
  authorizationCode: string | null;
  batchRefNumber: string | null;
  creditCardAlias: string | null;
  creditCardNumber: string;
  creditCardType: CreditCardType;
  datatransMerchantId: string | null;
  expirationMonth: number;
  expirationYear: number;
  gatewayProvider: string;
  nameOnCard: string | null;
  refNumber: string | null;
  transactionDateTime: DateTime<true>;
  transactionId: number;
};

export namespace CreditCardType {
  const values = ["VISA", "MASTERCARD", "AMEXCO"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CreditCardType = CreditCardType.Impl;

export type CustomPageAdminSearchForm = {
  includeInactive: boolean;
  onlyHighlighted: boolean;
  searchTerm: string;
};

export type CustomPageCopyRequest = {
  toCorporateId: number;
};

export type CustomPageEditForm = {
  active: boolean;
  customPageLocalization: { additionalProperties: Record<string, CustomPageLocalizationEditForm> };
  defaultCover: boolean;
  headerLayout: CustomPageHeaderLayout;
  portletIds: number[];
};

export type CustomPageEditListView = {
  active: boolean;
  corporateId: number;
  corporation: string;
  customPageId: bigint;
  highlightPriority: number | null;
  modifiedAt: DateTime<true>;
  titles: { additionalProperties: Record<string, string> };
};

export type CustomPageEditListViewArray = {
  arr: CustomPageEditListView[];
};

export type CustomPageEditView = {
  active: boolean;
  corporateId: number;
  coverUrl: string;
  customPageId: bigint;
  customPageLocalization: { additionalProperties: Record<string, CustomPageLocalizationEditView> };
  headerLayout: CustomPageHeaderLayout;
  portlets: PortletMetadata[];
};

export type CustomPageFilters = {
  includeInactive: boolean;
  onlyHighlighted: boolean;
};

export namespace CustomPageHeaderLayout {
  const values = ["NONE", "DEFAULT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CustomPageHeaderLayout = CustomPageHeaderLayout.Impl;

export type CustomPageHighlightsForm = {
  customPageIds: bigint[];
};

export type CustomPageListView = {
  corporateId: number;
  coverUri: string;
  customPageId: bigint;
  description: string | null;
  title: string;
};

export type CustomPageLocalizationEditForm = {
  description: string | null;
  title: string;
};

export type CustomPageLocalizationEditView = {
  description: string | null;
  title: string;
};

export type CustomPageView = {
  coverUrl: string;
  customPageId: bigint;
  description: string | null;
  headerLayout: CustomPageHeaderLayout;
  portlets: PortletMetadata[];
  title: string;
};

export type CustomerAdminAccountDetailForm = {
  acceptedTerms: DateTime<true> | null;
  customerId: number;
  isTestAccount: boolean;
  lastMobileUsage: DateTime<true> | null;
  lastWebUsage: DateTime<true> | null;
  lockedOutTime: bigint | null;
  loginAsCustomerUri: string;
  roles: CustomerAdminDetailRole[];
  totalWebUsages: number | null;
};

export type CustomerAdminB2BPortalsForm = {
  corporateId: number;
  corporation: string;
  corporationIsActive: boolean;
  customerIsActive: boolean;
  endDate: { year: number; month: number; day: number } | null;
  isExpired: boolean;
  responsibleCsm: string | null;
  responsibleSales: string | null;
  startDate: { year: number; month: number; day: number } | null;
  type: string | null;
};

export type CustomerAdminB2BPortalsForms = {
  forms: CustomerAdminB2BPortalsForm[];
};

export type CustomerAdminDetailAddress = {
  address1: string | null;
  address2: string | null;
  addressId: number;
  company: string | null;
  countryCode: string | null;
  department: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  position: string | null;
  state: string | null;
  title: number;
  town: string | null;
  vat: string | null;
  zipCode: string | null;
};

export type CustomerAdminDetailForm = {
  areaOfExpertise: AreaOfExpertise | null;
  batchEmails: boolean;
  billingAddress: CustomerAdminDetailAddress;
  careerLevel: CareerLevel | null;
  changePassword: boolean;
  commentNotification: boolean;
  customGender: string | null;
  customerId: number;
  education: boolean;
  email: string;
  gender: Gender | null;
  htmlEmail: boolean;
  invoiceByPost: boolean;
  language: "en" | "de" | "es" | "ru" | "zh" | "pt" | "fr";
  mailAddress: CustomerAdminDetailAddress;
  password: string;
  pushFrequency: PushFrequency;
  receiveNewsletters: boolean;
  unlimitedAccess: boolean;
  yearOfBirth: number | null;
};

export type CustomerAdminDetailRole = {
  corporation: string;
  customerEmail: string;
  isCorporationGroup: boolean;
  language: string;
  roleName: string;
};

export type CustomerAdminDetailWidgetProps = {
  customerId: number;
};

export type CustomerAdminFreeAbstractCoupon = {
  affiliateCorporateId: number;
  affiliateCorporation: string;
  affiliateUserName: string;
  couponBalance: number;
  couponRegistationBalance: number;
  registrationDate: { year: number; month: number; day: number };
  summaryGroupId: number;
};

export type CustomerAdminFreeAbstractCoupons = {
  coupons: CustomerAdminFreeAbstractCoupon[];
};

export type CustomerAdminListView = {
  active: boolean;
  address: string;
  b2bCustomer: boolean;
  country: string;
  customerId: number;
  email: string | null;
  firstName: string;
  lastName: string;
  portalStatuses: PortalStatusListView[];
  subscriptionStartDate: { year: number; month: number; day: number } | null;
  subscriptionStatus: string;
  subscriptionType: string;
  town: string;
  trialStartDate: { year: number; month: number; day: number } | null;
};

export type CustomerAdminListViewArray = {
  customerAdminListView: CustomerAdminListView[];
};

export type CustomerAdminMobileAppForm = {
  loginId: number;
  loginPreferences: MobileAppLoginPreferenceListView[];
  mobileDevices: MobileDevice[];
  oAuth2Tokens: OAuth2Token[];
  showLoginPreferences: boolean;
};

export type CustomerAdminQuickSearchResult = {
  count: number;
  uniqueId: number | null;
};

export type CustomerAdminSubscriptionsForm = {
  customerId: number;
  customerLanguageId: number;
  customerName: string;
  customerRandomCode: string;
  emailHistory: B2CRenewalEmails[];
  hasStudentSubscription: boolean;
  studentPreverification: StudentVerificationView | null;
  subscriptions: B2CSubscription[];
};

export type CustomerCommentForm = {
  comment: string;
  commentId: number;
  customerId: number;
  dateEntered: DateTime<true>;
  isMutable: boolean | null;
  operator: string | null;
};

export type CustomerCommentForms = {
  items: CustomerCommentForm[];
};

export type CustomerCommentSaveRequest = {
  comment: string;
};

export type CustomerMassImportEmailSetting = {
  hideUserData: boolean;
  sendMail: boolean;
};

export type CustomerMassImportEntryForm = {
  company: string | null;
  countryCode: string | null;
  customerId: number | null;
  email: string;
  endDate: { year: number; month: number; day: number } | null;
  extraField1: string | null;
  extraField2: string | null;
  extraField3: string | null;
  firstName: string;
  functionStr: string | null;
  isNew: boolean;
  language: "en" | "de" | "es" | "ru" | "zh" | "pt" | "fr";
  lastName: string;
  nameId: string | null;
  password: string | null;
  state: string | null;
  title: number;
  town: string | null;
  userGroups: number[];
};

export type CustomerMassImportForm = {
  emailSetting: CustomerMassImportEmailSetting;
  items: CustomerMassImportEntryForm[];
  pushFrequencySetting: CustomerMassImportPushFrequencySetting;
};

export type CustomerMassImportProcessResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS" };

export type CustomerMassImportPushFrequencySetting = {
  digestFrequency: PushFrequency;
  updateExistingUserDigestFrequency: boolean;
};

export type CustomerMassImportUploadResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; items: CustomerMassImportView[] };

export type CustomerMassImportView = {
  form: CustomerMassImportEntryForm;
  status: "OK" | "WARNING" | "ERROR";
  statusInfo: string;
};

export type CustomerReportView = {
  country: string | null;
  customerId: number;
  email: string | null;
  endDate: { year: number; month: number; day: number };
  externalId: string | null;
  extraField1: string | null;
  extraField2: string | null;
  extraField3: string | null;
  firstName: string | null;
  functionStr: string | null;
  language: Language;
  lastName: string | null;
  signupDate: { year: number; month: number; day: number };
  state: string | null;
  status: string;
  title: number | null;
  town: string | null;
  userGroups: string[];
};

export type CustomerSearchForm = {
  accountNumber: string;
  creditCardFirstDigits: string;
  creditCardLastDigits: string;
  customerId: number | null;
  email: string;
  firstName: string;
  lastName: string;
  paymentType: "ALL" | "PAYPAL" | "IOS" | "ANDROID" | "CC" | "LSV" | "INVOICE" | "MILES" | "ALIPAY";
  phoneNumber: string;
  refNo: string;
  subscriptionId: number | null;
  transactionEndDate: { year: number; month: number; day: number } | null;
  transactionId: number | null;
  transactionStartDate: { year: number; month: number; day: number } | null;
};

export type CustomerView = {
  customerId: number;
  email: string;
  fullName: string;
};

export type DateFormattingDemoProps = {
  values: {
    label: string;
    value: string;
  }[];
};

export type DateRange = {
  from: { year: number; month: number; day: number };
  to: { year: number; month: number; day: number };
};

export type DemoFormCheckbox = {
  choose: string[];
  obligatoryAccept: boolean;
  option: boolean;
};

export type DemoFormDate = {
  obligatoryDate: { year: number; month: number; day: number };
  publishDate: { year: number; month: number; day: number } | null;
};

export type DemoFormFile = {
  data: ReadonlyUint8Array;
};

export type DemoFormNumber = {
  age: number;
  mayBeEmpty: number | null;
};

export type DemoFormPassword = {
  password: string;
  repeatPassword: string;
};

export type DemoFormRadio = {
  choose: "YES" | "MAYBE" | "NO";
  noDefaultChoose: ("THIS" | "THAT") | null;
};

export type DemoFormSelect = {
  noDefaultSelect: ("FIRST_ENTRY" | "SECOND_ENTRY" | "THIRD_ENTRY" | "FOURTH_ENTRY" | "FIFTH_ENTRY" | "SIXTH_ENTRY" | "SEVENTH_ENTRY" | "EIGHTH_ENTRY" | "NINTH_ENTRY" | "TENTH_ENTRY" | "ELEVENTH_ENTRY" | "TWELFTH_ENTRY") | null;
  select: "RED" | "BLUE" | "GREEN";
};

export type DemoFormText = {
  author: string;
  email: string;
  title: string | null;
};

export type DemoFormTextarea = {
  comment: string;
};

export type DisplayMonetaryAmount = {
  amount: number;
  currencyCode: string;
  displayValue: string;
};

export type DomainProtectionForm = {
  checkDomains: boolean;
  domains: string[];
};

export type DomainProtectionFormSaveResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; form: DomainProtectionForm };

export namespace DownloadType {
  const values = ["PUSH", "PULL", "BOTH"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type DownloadType = DownloadType.Impl;

export type DropdownEntry = {
  text: string;
  value: string | null;
};

export type DropdownEntryList = {
  dropDownEntryList: DropdownEntry[];
};

export type EmailCategoryFilters = {
  categoryId: number | null;
  categoryName: string | null;
  followUpType: EmailCategoryFollowUpType;
};

export namespace EmailCategoryFollowUpType {
  const values = ["STANDARD", "INTERVAL", "DATEBASED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryFollowUpType = EmailCategoryFollowUpType.Impl;

export type EmailCategoryForm = {
  description: string | null;
  details: EmailCategoryFormDetails;
  name: string;
  repositoryId: number;
  sendCountriesOnly: string[];
  sendMatchingLanguageOnly: boolean;
};

export type EmailCategoryFormDetails =
  | { repositoryType: "DEFAULT" }
  | { repositoryType: "RENEWAL"; active: boolean; maxDaysAfterExpiration: number; minDaysAfterExpiration: number; productIds: number[] }
  | { repositoryType: "EDUCATION"; active: boolean; productIds: number[]; welcomeEmail: boolean }
  | { repositoryType: "ONBOARDING"; active: boolean; maxDaysAfterSignup: number; minDaysAfterSignup: number }
  | {
      repositoryType: "FOLLOWUP";
      active: boolean;
      corporateIds: number[];
      emailingPools: string[];
      followupType: EmailCategoryFollowUpType;
      interval: number;
      maxDaysAfterExpiration: number;
      minDaysAfterExpiration: number;
      sendToAll: boolean;
      startDate: { year: number; month: number; day: number } | null;
    }
  | { repositoryType: "MANUAL"; corporateIds: number[] };

export type EmailCategoryManualSendForm = {
  executionDate: { year: number; month: number; day: number } | null;
  notifyEmail: string;
};

export namespace EmailCategoryTypeDefault {
  const values = ["DEFAULT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeDefault = EmailCategoryTypeDefault.Impl;

export namespace EmailCategoryTypeEducation {
  const values = ["EDUCATION"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeEducation = EmailCategoryTypeEducation.Impl;

export namespace EmailCategoryTypeFollowUp {
  const values = ["FOLLOWUP"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeFollowUp = EmailCategoryTypeFollowUp.Impl;

export namespace EmailCategoryTypeManual {
  const values = ["MANUAL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeManual = EmailCategoryTypeManual.Impl;

export namespace EmailCategoryTypeOnboarding {
  const values = ["ONBOARDING"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeOnboarding = EmailCategoryTypeOnboarding.Impl;

export namespace EmailCategoryTypeRenewal {
  const values = ["RENEWAL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeRenewal = EmailCategoryTypeRenewal.Impl;

export type EmailDefaultCategoryAdminView = {
  categoryId: number;
  name: string;
  repositoryId: number;
};

export type EmailEducationCategoryAdminView = {
  active: boolean;
  categoryId: number;
  name: string;
  productIds: number[];
  repositoryId: number;
};

export type EmailFollowUpCategoryAdminView = {
  active: boolean;
  categoryId: number;
  corporateIds: string[];
  interval: number;
  maxDaysAfterExpiration: number;
  minDaysAfterExpiration: number;
  name: string;
  repositoryId: number;
  startDate: { year: number; month: number; day: number } | null;
};

export type EmailManualCategoryAdminView = {
  categoryId: number;
  corporateIds: string[];
  name: string;
  repositoryId: number;
};

export type EmailOnboardingCategoryAdminView = {
  active: boolean;
  categoryId: number;
  maxDaysAfterSignup: number;
  minDaysAfterSignup: number;
  name: string;
  repositoryId: number;
};

export type EmailPoolAdminCreateResult = { kind: "DUPLICATE_ERROR" } | { kind: "SUCCESS"; data: EmailPoolAdminView };

export type EmailPoolAdminListView = {
  items: EmailPoolAdminView[];
};

export type EmailPoolAdminUpdateResult = { kind: "ITEM_NOT_FOUND_ERROR" } | { kind: "SUCCESS"; data: EmailPoolAdminView };

export type EmailPoolAdminView = {
  corporateIds: number[];
  name: string;
};

export type EmailPoolForm = {
  corporateIds: number[];
};

export type EmailRenewalCategoryAdminView = {
  active: boolean;
  categoryId: number;
  maxDaysAfterExpiration: number;
  minDaysAfterExpiration: number;
  name: string;
  productIds: number[];
  repositoryId: number;
};

export type EmailRepositoryAdminListView = {
  items: EmailRepositoryAdminView[];
};

export type EmailRepositoryAdminView = {
  comment: string | null;
  name: string;
  repositoryId: number;
  type: EmailRepositoryType;
};

export namespace EmailRepositoryType {
  const values = ["DEFAULT", "FOLLOWUP", "RENEWAL", "EDUCATION", "ONBOARDING", "MANUAL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailRepositoryType = EmailRepositoryType.Impl;

export type EmailTemplateCategoryInfo = {
  alternativeRecipientDefault: string;
  categoryName: string;
  defaultSender: string;
  footerOptions: DropdownEntry[];
  repositoryId: number;
  repositoryName: string;
  runningBatchProcess: boolean;
  templates: EmailTemplateEditView[];
};

export type EmailTemplateEditView = {
  analyticsCampaign: string | null;
  attachmentPath: string | null;
  contentHtml: string | null;
  contentPlain: string | null;
  contentSidebar: string | null;
  emailFrameId: number | null;
  emailTitle: string | null;
  footerSnippet: string | null;
  language: Language;
  linkTemplateCategoryId: number | null;
  linkTemplateId: number | null;
  sender: string | null;
  subject: string;
  templateId: number;
  templateLanguage: "SNIPPETS" | "VELOCITY";
};

export type EmailTemplateForm = {
  analyticsCampaign: string | null;
  attachmentPath: string | null;
  contentHtml: string | null;
  contentPlain: string | null;
  contentSidebar: string | null;
  emailFrameId: number | null;
  emailTitle: string | null;
  footerSnippet: string | null;
  language: Language;
  linkTemplateId: number | null;
  sender: string | null;
  subject: string;
  templateLanguage: "SNIPPETS" | "VELOCITY";
};

export type EmailTemplateTestForm = {
  alternativeRecipient: string | null;
  corporateId: number | null;
  customerId: number;
  dataIds: number[];
  format: EmailTemplateTestFormat;
  method: EmailTemplateTestMethod;
  subscriptionId: number | null;
};

export namespace EmailTemplateTestFormat {
  const values = ["HTML", "PLAINTEXT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailTemplateTestFormat = EmailTemplateTestFormat.Impl;

export namespace EmailTemplateTestMethod {
  const values = ["BROWSER", "EMAIL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailTemplateTestMethod = EmailTemplateTestMethod.Impl;

export type EmailTemplateTestResult = {
  format: EmailTemplateTestFormat;
  method: EmailTemplateTestMethod;
  resultBody: string | null;
  success: boolean;
};

export type ExpertVideoAdminForm = {
  aboutTheHost: string;
  active: boolean;
  authorImage: FormFile | null;
  eventDateTime: ZonedDateTime;
  expertVideoId: bigint;
  host: string;
  language: Language;
  length: number;
  pdf: FormFile | null;
  registrationLink: string;
  tableOfContent: {
    timestamp: number;
    title: string;
  }[];
  teaser: string;
  title: string;
  transcript: string;
  vimeoLink: string;
  whatYouLearn: string;
  youtubeLink: string;
};

export type ExpertVideoAdminOverview = {
  items: ExpertVideoAdminForm[];
};

export type ExpertVideoDetailPageProps = {
  expertVideo: ExpertVideoDetailView;
};

export type ExpertVideoDetailView = {
  aboutTheHost: string;
  authorImage: FormFile | null;
  eventDateTime: ZonedDateTime;
  expertVideoId: bigint;
  host: string;
  length: number;
  metadata: ContentMetadata;
  pdf: FormFile | null;
  registrationLink: string;
  tableOfContent: {
    timestamp: number;
    title: string;
  }[];
  transcript: string;
  vimeoLink: string;
  whatYouLearn: string;
  youtubeLink: string;
};

export type ExpertVideoEditPageProps = {
  availableTimeZones: DropdownEntry[];
  expertVideo: ExpertVideoAdminForm;
};

export type ContentId_EXPERT_VIDEO = {
  contentType: "EXPERT_VIDEO";
  id: bigint;
};

export type ExpertVideoPagingInfo = {
  pageEnglish: PageInfo;
  pageNative: PageInfo;
};

export type ExpertVideoSaveResponse = { kind: "ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; expertVideoId: bigint };

export type ExpertVideosResult = {
  expertVideosEnglish: ContentMetadata_EXPERT_VIDEO[];
  expertVideosNative: ContentMetadata_EXPERT_VIDEO[];
  pagingEnglish: PageInfo;
  pagingNative: PageInfo;
};

export type ExploreNavigationItem = {
  selected: boolean;
  title: string;
  url: string;
};

export type ExploreNavigationProps = {
  navigationItems: {
    item: ExploreNavigationItem;
    subItems: {
      item: ExploreNavigationItem;
    }[];
  }[];
  selected: boolean;
  title: string;
  url: string;
};

export type ExplorePageProps = {
  groupings: {
    items: SkillOverviewItem[];
    subTitle: string;
    title: string;
  }[];
  overview: SkillGroups;
};

export type ExportReportListView = {
  downloadLink: string;
  exportDate: { year: number; month: number; day: number };
  exportTaskId: number;
  fileName: string;
  hash: string;
  reportName: string | null;
  reportType: ReportType | null;
};

export namespace FeatureType {
  const values = ["ACTIONABLE", "GET_ABSTRACT_AI", "MIAB"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FeatureType = FeatureType.Impl;

export type FeatureTypeArr = {
  items: FeatureType[];
};

export type FilteredLearningItemAdminView = {
  items: LearningItemAdminView[];
  totalCount: number;
};

export type FormFile = {
  bytes: ReadonlyUint8Array;
  filename: string;
  mimeType: string;
};

export type FormFilter = {
  activeValues: string[];
  identifier: FormFilterName;
  label: string;
  name: string;
  options: FormFilterOption[];
  priority: FormFilterPriority;
  type: FormFilterType;
};

export namespace FormFilterName {
  const values = ["CHANNEL", "SOURCE", "QUALITY", "RATING", "AUDIO", "LANGUAGE", "PUBLICATION_DATE", "MIAB"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FormFilterName = FormFilterName.Impl;

export type FormFilterOption = {
  label: string;
  shortLabel: string | null;
  value: string;
};

export namespace FormFilterPriority {
  const values = ["PRIMARY", "SECONDARY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FormFilterPriority = FormFilterPriority.Impl;

export namespace FormFilterType {
  const values = ["MULTICHECKBOX", "CHECKBOX", "MULTISELECT", "RANGE", "RANGE_MIN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FormFilterType = FormFilterType.Impl;

export type FormSorting = {
  activeValue: string;
  options: FormSortingOption[];
};

export type FormSortingOption = {
  label: string;
  value: string;
};

export type FreeSummaryPortletView = {
  metadata: PortletMetadata;
  summary: ContentMetadata_SUMMARY | null;
  viewAllUri: string | null;
};

export type FreelancerContactFilterForm = {
  languages: Language[];
  managingEditorCustomerId: number | null;
  writerStates: WriterState[];
};

export type FreelancerContactForm = {
  attachments: GaFileObject[];
  emailContent: string;
  emailSubject: string;
  managingEditorCustomerId: number | null;
  writerCustomerIds: number[];
};

export type FreelancerContactWriterListView = {
  writers: FreelancerContactWriterView[];
};

export type FreelancerContactWriterView = {
  languages: string;
  writer: UserListView;
  writerState: WriterState;
};

export namespace FrontendProject {
  const values = ["VUE", "DESIGN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FrontendProject = FrontendProject.Impl;

export type FullSearchProps = {
  beta: boolean;
  hasCorporatePlus: boolean;
  initialActionableFormFilters: FormFilter[];
  initialSummaryFormFilters: FormFilter[];
  query: string | null;
};

export type FullSearchResults = {
  actionableFormFilters: FormFilter[];
  actionables: ContentMetadata[];
  channelPaging: {
    page: number;
    totalCount: bigint;
  };
  channels: ChannelMetadata[];
  customPages: CustomPageListView[];
  downloadSourceParams: { additionalProperties: Record<string, string> };
  summaries: ContentMetadata[];
  summariesPaging: {
    page: number;
    totalCount: bigint;
  };
  summaryFormFilters: FormFilter[];
};

export type GaContextAsp = {
  corporateId: number;
  username: string;
};

export type GaContextCustomer = {
  customerId: number;
};

export type GaContextDataSync = {
  asp: GaContextAsp | null;
  currentLanguage: Language;
  customer: GaContextCustomer | null;
  flags: GaContextFlags;
};

export type GaContextFlags = {
  beta: boolean;
  loggedIn: boolean;
};

export type GaFileObject = {
  bytes: ReadonlyUint8Array;
  contentType: string;
  originalFileName: string;
};

export namespace Gender {
  const values = ["FEMALE", "MALE", "UNKNOWN", "CUSTOM"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type Gender = Gender.Impl;

export type GenerateMiabAdminResponse = {
  content: MIABContent;
};

export namespace GiftCodeStatus {
  const values = ["AVAILABLE", "UNAVAILABLE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type GiftCodeStatus = GiftCodeStatus.Impl;

export type GiftDonorSubscription = {
  donorCustomerId: number;
  donorSubscriptionId: number;
};

export type GiftReceiverSubscription = {
  receiverCustomerId: number;
  receiverSubscriptionId: number;
};

export type GiftSubscription = {
  donorSubscription: GiftDonorSubscription | null;
  giftReceiverSubscription: GiftReceiverSubscription | null;
};

export type Invoice = {
  generatedAt: DateTime<true>;
  invoiceId: number;
  invoiceLevel: number;
  invoiceUri: string | null;
  pdfCounter: number;
  transactionId: number;
};

export type InvoicePaymentCardView = {
  additionalInformationBottom: string | null;
  additionalInformationTop: string | null;
  bankConnectionDetails: BankConnectionDetails;
  invoiceIssuerId: number;
  issuerAddress: Address;
  total: MonetaryAmount;
};

export type InvoicePaymentCardViewList = {
  invoices: InvoicePaymentCardView[];
};

export type InvoicePaymentParameters = {
  invoiceIds: bigint[];
  paidAt: { year: number; month: number; day: number };
};

export type InvoicePaymentView = {
  paidAt: DateTime<true> | null;
  reference: string | null;
  transactionId: number;
};

export type IosPaymentView = {
  appStoreProductId: string;
  autoRenewProductId: string | null;
  autoRenewing: boolean;
  expirationIntent: number;
  itunesTransactionId: string;
  originalItunesTransactionId: string | null;
  testPurchase: boolean;
  transactionId: number;
  webOrderLineItemId: bigint;
};

export type IpProtectionForm = {
  checkIp: boolean;
  ips: string[];
};

export type IpProtectionFormSaveResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; form: IpProtectionForm };

export namespace ItemLayout {
  const values = ["MINI", "REGULAR"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ItemLayout = ItemLayout.Impl;

export namespace KindChannel {
  const values = ["channel"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindChannel = KindChannel.Impl;

export namespace KindClassification {
  const values = ["classification"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindClassification = KindClassification.Impl;

export namespace KindDynamicBoolean {
  const values = ["dynamicBoolean"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindDynamicBoolean = KindDynamicBoolean.Impl;

export namespace KindDynamicNumber {
  const values = ["dynamicNumber"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindDynamicNumber = KindDynamicNumber.Impl;

export namespace KindDynamicString {
  const values = ["dynamicString"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindDynamicString = KindDynamicString.Impl;

export namespace KindDynamicVoid {
  const values = ["dynamicVoid"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindDynamicVoid = KindDynamicVoid.Impl;

export namespace KindKeyQuality {
  const values = ["key-quality"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindKeyQuality = KindKeyQuality.Impl;

export namespace KindPrimarySkill {
  const values = ["primary-skill"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindPrimarySkill = KindPrimarySkill.Impl;

export namespace KindSkill {
  const values = ["skill"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindSkill = KindSkill.Impl;

export namespace Language {
  const values = ["en", "de", "es", "ru", "zh", "pt", "fr"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type Language = Language.Impl;

export type LeadCreationResponse = { kind: "ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "MEETING"; calendlyUrl: string } | { kind: "SUCCESS" };

export type LeadForm = {
  comment: string;
  company: string;
  companySize: LeadFormCompanySize;
  country: string;
  email: string;
  firstName: string;
  function: string;
  gRecaptchaResponse: string;
  howDidYouFindUs: string;
  inquiry: LeadFormInquiry;
  lastName: string;
  leadSource: LeadSource;
  phone: string;
  utmCampaign: string;
  utmContent: string;
  utmId: string;
  utmMedium: string;
  utmSource: string;
  utmTerm: string;
};

export namespace LeadFormCompanySize {
  const values = ["PLEASE_CHOOSE", "1_10", "11_100", "101_500", "501_1000", "1001_5000", "5000PLUS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LeadFormCompanySize = LeadFormCompanySize.Impl;

export namespace LeadFormInquiry {
  const values = ["PLEASE_CHOOSE", "FOR_ENTERPRISE", "FOR_INDIVIDUALS", "FOR_TEAMS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LeadFormInquiry = LeadFormInquiry.Impl;

export type LeadFormProps = {
  countries: Country[];
  country: Country;
  fullWidth: boolean;
  lead: string | null;
  leadSource: LeadSource;
  title: string | null;
};

export namespace LeadSource {
  const values = [
    "RENEWAL_FAILED",
    "CANCELATION",
    "CONTACTFORM",
    "MANUAL",
    "TRIAL_REQUEST",
    "WHITE_PAPER_DOWNLOAD",
    "FREE_ABSTRACTS",
    "PURCHASE_FAILED",
    "PURCHASE_FAILED_OLD",
    "ACTIONABLE_LANDING_PAGE",
    "CUSTOMER_STORIES_LANDING_PAGE",
    "SUPPORT_FORM",
    "LEADERSFUTURE_LANDING_PAGE",
    "TEAMDEVELOPMENT_LANDING_PAGE",
    "EMPLOYEEENGAGEMENT_LANDING_PAGE",
    "EMPLOYEETRAINING_LANDING_PAGE",
    "CORPORATELEARNING_LANDING_PAGE",
    "LEARNINGDEVELOPMENT_LANDING_PAGE",
    "MICROLEARNING_LANDING_PAGE",
    "SKILLSOFT_LANDING_PAGE",
    "STROER_LANDING_PAGE",
    "LMS_LANDINGPAGE",
    "MICROLEARNING_LANDINGPAGE",
    "ENTERPRISE_PRODUCTS",
    "SOLUTIONS_PAGE",
    "SOLUTIONS_UPSKILLING",
    "SOLUTIONS_DEI",
    "SOLUTIONS_HEALTH_WELLBEING",
    "SOLUTIONS_LEARNING_PLATFORM_STRATEGY",
    "SOLUTIONS_TALENT_DEVELOPMENT",
    "SOLUTIONS_COLLABORATIVE_LEARNING",
    "SOLUTIONS_LEARNING_RESOURCE_LIBRARY",
    "SOLUTIONS_EFFECTIVE_LEADERS",
    "WELCOME_PAGE",
    "BUSINESS_BOOK_SUMMARY_LANDINGPAGE",
    "GRAPH_CONNECTOR_LANDINGPAGE",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LeadSource = LeadSource.Impl;

export type LearningCalendarItem = {
  coverImage: string;
  coverUri: string;
  date: { year: number; month: number; day: number };
  dayOfMonth: number;
  icon: string;
  isFirstAvailableDoor: boolean;
  isWeekend: boolean;
  linkUri: string;
  status: "OPEN" | "CLOSED" | "WEEKEND" | "DISABLED";
};

export type LearningCalendarView = {
  bannerImgUri: string;
  doors: LearningCalendarItem[];
  endDate: { year: number; month: number; day: number } | null;
  expiresAt: { year: number; month: number; day: number } | null;
  learningCalendarId: bigint;
  name: string;
  startDate: { year: number; month: number; day: number };
};

export type LearningItemAdminActionableView = {
  description: string;
  learningItemType: LearningItemTypeActionable;
  metadata: LearningItemAdminMetadataView;
};

export type LearningItemAdminFilter = {
  activatedAtRange: DateRange | null;
  active: boolean | null;
  audio: boolean | null;
  createdAtRange: DateRange | null;
  documentTypes: SummaryDocumentType[];
  ids: number[];
  itemTypes: LearningItemType[];
  languages: Language[];
  manager: string | null;
  missingTagKinds: TagKind[];
  publisher: string | null;
  resultSize: number;
  sourceTypes: SummarySourceType[];
  tagCount: number | null;
  tagKinds: TagKind[];
  tags: Tag[];
  title: string | null;
};

export type LearningItemAdminMetadataView = {
  activatedAt: { year: number; month: number; day: number } | null;
  active: boolean;
  createdAt: { year: number; month: number; day: number };
  deactivatedAt: { year: number; month: number; day: number } | null;
  id: bigint;
  language: Language;
  manager: string | null;
  tags: Tag[];
  title: string;
};

export type LearningItemAdminProps = {
  admin: boolean;
  filter: LearningItemAdminFilter;
  views: ("READONLY" | "LEARNING_ITEM_VIEW" | "TAG_VIEW" | "CHANNEL_VIEW")[];
};

export type LearningItemAdminSummaryView = {
  audioActive: boolean;
  documentType: SummaryDocumentType;
  learningItemType: LearningItemTypeSummary;
  metadata: LearningItemAdminMetadataView;
  publisher: string | null;
  sourceType: SummarySourceType;
};

export type LearningItemAdminView =
  | { learningItemType: "ACTIONABLE"; description: string; metadata: LearningItemAdminMetadataView }
  | { learningItemType: "SUMMARY"; audioActive: boolean; documentType: SummaryDocumentType; metadata: LearningItemAdminMetadataView; publisher: string | null; sourceType: SummarySourceType };

export namespace LearningItemType {
  const values = ["SUMMARY", "ACTIONABLE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LearningItemType = LearningItemType.Impl;

export namespace LearningItemTypeActionable {
  const values = ["ACTIONABLE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LearningItemTypeActionable = LearningItemTypeActionable.Impl;

export namespace LearningItemTypeSummary {
  const values = ["SUMMARY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LearningItemTypeSummary = LearningItemTypeSummary.Impl;

export type ContentId_SKETCHNOTE = {
  contentType: "SKETCHNOTE";
  id: bigint;
};

export type ContentMetadata_SKETCHNOTE = {
  common: ContentMetadataCommon;
  contentId: ContentId_SKETCHNOTE;
  contentType: "SKETCHNOTE";
};

export type LibraryConnectorListView = {
  active: boolean;
  connectorId: number;
  connectorName: string;
  connectorType: "OCN" | "DOCEBO" | "XAPI" | "CSOD" | "DEGREED";
  corporateId: number;
  corporation: string;
  createdAt: DateTime<true>;
  modifiedAt: DateTime<true> | null;
};

export type LibraryConnectorListViewArray = {
  libraryConnectorListView: LibraryConnectorListView[];
};

export type LsvPaymentView = {
  accountNumber: string | null;
  acqAuthorizationCode: string | null;
  authorizationCode: string | null;
  bankIban: string | null;
  bankName: string | null;
  bankTown: string | null;
  clearingNumber: string | null;
  firstName: string;
  lastName: string;
  merchantId: string | null;
  refNumber: string | null;
  transactionId: number;
};

export type MIABAnalyticsFeedbackEventForm = {
  isPositive: boolean;
  miabId: bigint;
};

export type MIABContent = {
  data: MIABContentData;
  miabId: bigint;
};

export type MIABContentData = {
  customerId: number | null;
  keyInsights: string[];
  modifiedAt: DateTime<true>;
  questions: MIABQuestionMetadata[];
  summaryId: number;
};

export type MIABContents = {
  items: ContentMetadata_MIAB[];
};

export type MIABCustomQuestionsForm = {
  dataId: number;
  meta: string;
  prompt: string;
  questions: MIABQuestionMetadata[];
};

export type MIABCustomQuestionsProps = {
  createdAt: DateTime<true>;
  createdBy: string;
  customQuestions: MIABQuestionMetadata[];
  customerId: number;
  hash: string;
};

export type MIABCustomQuestionsResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "CREATED"; hash: string; miabId: bigint } | { kind: "UPDATED"; miabId: bigint };

export type MIABOverviewPageProps = {
  channels: ChannelMetadata[];
};

export type MIABPageProps = {
  ixStatuses: ContentIxStatus[];
  linkedMiab: ContentMetadata_MIAB | null;
  miab: MIABView;
  openView: ("AI" | "EDIT") | null;
  regionalRestriction: {
    countriesByRegion: { additionalProperties: Record<string, string[]> };
    restrictionType: CountryRestrictionType;
  };
  similarMiabs: ContentMetadata[];
};

export type MIABPopularSummariesResult = {
  miabsEnglish: ContentMetadata[];
  miabsNative: ContentMetadata[];
  pagingEnglish: PageInfo;
  pagingNative: PageInfo;
};

export type MIABPrepareStepAiResponse = {
  point1: string;
  point2: string;
  point3: string;
  point4: string;
};

export type MIABQuestionMetadata = {
  createdAt: DateTime<true> | null;
  question: string;
  title: string;
};

export type MIABQuestionsAiResponse = {
  question1: {
    question: string;
    title: string;
  };
  question2: {
    question: string;
    title: string;
  };
  question3: {
    question: string;
    title: string;
  };
  question4: {
    question: string;
    title: string;
  };
  question5: {
    question: string;
    title: string;
  };
};

export type MIABSummaryForm = {
  pageEnglish: number;
  pageNative: number;
  selectedChannel: bigint | null;
};

export type MIABUserContext = {
  customerId: number;
  hash: string;
  username: string;
};

export type MIABView = {
  content: MIABContent;
  interactions: ContentIxStatus_MIAB | null;
  metadata: ContentMetadata;
  userContext: MIABUserContext | null;
};

export type MiabAiSuggestionRequest = {
  dataId: number;
  userPrompt: string;
};

export type MiabAiSuggestionResponse = {
  questions: MIABQuestionMetadata[];
};

export type MiabFiltersForm = {
  languageFormFilter: Language[];
};

export type ContentId_MIAB = {
  contentType: "MIAB";
  id: bigint;
};

export type ContentMetadata_MIAB = {
  common: ContentMetadataCommon;
  contentId: ContentId_MIAB;
  contentType: "MIAB";
  customerId: number | null;
  dataId: number;
};

export type MilesPaymentView = {
  cardNumber: string | null;
  milesTransactionId: number | null;
  pin: string | null;
  transactionId: number;
};

export type MobileAppLoginPreferenceListView = {
  corporateId: number;
  corporationName: string;
};

export type MobileDevice = {
  corporateId: number;
  customerId: number;
  deviceId: string;
  deviceType: string;
  firstUsedAt: DateTime<true>;
  language: Language;
  lastPushAt: DateTime<true> | null;
  lastUsedAt: DateTime<true>;
  osType: string | null;
  pushFrequency: PushFrequency;
  pushToken: string | null;
  version: string;
};

export type MonetaryAmount = {
  amount: number;
  currencyCode: string;
  displayValue: string;
};

export type MyListPortletView = {
  items: MyListView[];
  metadata: PortletMetadata;
  page: number;
  pageSize: number;
  totalCount: bigint;
  viewAllUri: string | null;
};

export type MyListView = {
  coverUri: string;
  key: string;
  myListId: bigint;
  name: string;
};

export type MyProductInformation = {
  corporatePlus: boolean;
  currency: string;
  currencyId: number;
  displayDiscount: string;
  displayPrice: string;
  extraMonths: number;
  gift: boolean;
  giftQuantity: number;
  isDiscount: boolean;
  lengthText: string;
  months: number;
  productGroupId: number;
  productName: string;
  productSummary: string;
  promotionCode: string | null;
  promotionCodeInvisible: boolean;
  promotionCodeRenew: boolean;
  quantity: string;
  seats: number;
  seatsText: string;
  subscriptionPeriodFormatted: string;
  teamProduct: boolean;
};

export namespace NavigationDirection {
  const values = ["NEXT", "PREVIOUS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type NavigationDirection = NavigationDirection.Impl;

export type NewB2BReportForm = {
  active: boolean;
  activeUsersOnly: boolean;
  b2bCustomReport: boolean;
  ccRecipients: string;
  corporateId: number;
  customerId: number;
  dayOfWeek: number;
  downloadType: DownloadType;
  emailAdministrator: string | null;
  frequency: ReportFrequency;
  fromDate: { year: number; month: number; day: number } | null;
  ftpFolder: string;
  ftpHost: string;
  ftpPassphrase: string;
  ftpPassword: string;
  ftpPort: number;
  ftpRemoteFileName: string;
  ftpUserName: string;
  prefixFileName: boolean;
  privateKey: string | null;
  privateKeyContent: string;
  privateKeyEnabled: boolean;
  reportId: number;
  reportName: string;
  reportType: ReportType;
  specialReport: boolean;
  targetExcel: boolean;
  toDate: { year: number; month: number; day: number } | null;
  transferProtocol: TransferProtocol;
  zipFile: boolean;
};

export type OAuth2Token = {
  accessTokenExpiredAt: DateTime<true> | null;
  accessTokenValue: string | null;
  corporateId: number;
  isExpired: boolean;
  refreshTokenExpiredAt: DateTime<true> | null;
  refreshTokenValue: string | null;
  tokenType: string | null;
};

export type OutboundLink = {
  id: bigint | null;
  meta: string;
  targetName: string | null;
  targetUrl: string;
  type: OutboundLinkType;
};

export namespace OutboundLinkType {
  const values = ["GENERIC", "BUYBOOK", "MIAB_SCHEDULE", "ACTIONABLE_SCHEDULE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OutboundLinkType = OutboundLinkType.Impl;

export type PageInfo = {
  page: number;
  pageSize: number;
  totalCount: number;
};

export type PageRequest = {
  page: number;
  psize: number;
  sortBy: string | null;
  sortDesc: boolean;
};

export type PageViewMeta = {
  ci: bigint | null;
  ct: ContentItemType;
  orr: string;
  p: string;
  r: string | null;
};

export type PageViewPayload = {
  iframe: boolean;
  meta: string;
  timezone: string;
};

export type PagedActionableMetadata = {
  items: ContentMetadata_ACTIONABLE[];
  pageInfo: PageInfo;
};

export type PagedAuthorAdminListView = {
  items: AuthorAdminListView[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedConnectorCompletionExecutionLogListView = {
  items: ConnectorCompletionExecutionLogListView[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedConnectorContentExecutionListView = {
  items: ConnectorContentExecutionListView[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedConnectorContentExecutionLogListView = {
  items: ConnectorContentExecutionLogListView[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedCustomPageEditListView = {
  items: CustomPageEditListView[];
  pageInfo: PageInfo;
};

export type PagedCustomPageListView = {
  items: CustomPageListView[];
  pageInfo: PageInfo;
};

export type PagedEmailDefaultCategoryAdminView = {
  items: EmailDefaultCategoryAdminView[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedEmailEducationCategoryAdminView = {
  items: EmailEducationCategoryAdminView[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedEmailFollowUpCategoryAdminView = {
  items: EmailFollowUpCategoryAdminView[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedEmailManualCategoryAdminView = {
  items: EmailManualCategoryAdminView[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedEmailOnboardingCategoryAdminView = {
  items: EmailOnboardingCategoryAdminView[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedEmailRenewalCategoryAdminView = {
  items: EmailRenewalCategoryAdminView[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedExportReportListView = {
  items: ExportReportListView[];
  pageInfo: PageInfo;
};

export type PagedPeerInsightFeedbackListView = {
  items: PeerInsightFeedbackListView[];
  page: PageInfo;
};

export type PagedReportListView = {
  items: ReportListView[];
  pageInfo: PageInfo;
};

export type PagedSummaryListView = {
  items: SummaryListView[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedSummaryMetadata = {
  items: ContentMetadata_SUMMARY[];
  pageInfo: PageInfo;
};

export type PagedVerifaiCreatorOverview = {
  items: VerifaiCreatorOverviewItem[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PagedVerifaiOverview = {
  items: VerifaiOverviewItem[];
  page: number;
  pageSize: number;
  totalCount: bigint;
};

export type PayPalPaymentView = {
  acqAuthorizationCode: string | null;
  authorizationCode: string | null;
  merchantId: string | null;
  paypalBillingAgreementId: string | null;
  refNumber: string | null;
  transactionId: number;
};

export namespace PaymentType {
  const values = ["CC", "INVOICE", "PAYPAL", "LSV", "MILES", "ALIPAY", "IOS", "ANDROID"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PaymentType = PaymentType.Impl;

export type PeerInsightFeedbackDashboardView = {
  areaOfExpertise: AreaOfExpertise;
  feedbackText: string;
  likeCount: number;
  liked: boolean;
  peerInsightFeedbackId: bigint;
  question: PeerInsightQuestionView;
};

export type PeerInsightFeedbackListView = {
  actionableId: bigint;
  actionableTitle: string;
  areaOfExpertise: AreaOfExpertise;
  creationDate: DateTime<true>;
  feedbackIndex: number | null;
  feedbackScore: number | null;
  feedbackText: string | null;
  peerInsightFeedbackId: bigint;
  question: PeerInsightQuestionView;
  status: ApprovalStatus | null;
};

export type PeerInsightFeedbackUpdateRequest = {
  approvalStatus: ApprovalStatus;
  peerInsightFeedbackId: bigint;
};

export type PeerInsightFormNavigation = {
  navigationDirection: NavigationDirection;
  peerInsightFormSteps: PeerInsightFormStep[];
  peerInsightQuestionId: bigint;
};

export type PeerInsightFormStep = {
  hasNext: boolean;
  hasPrevious: boolean;
  question: PeerInsightQuestionView;
  selectedScore: number | null;
  selectedSuggestedAnswers: bigint[];
  suggestedAnswers: SuggestedAnswerView[];
  text: string;
};

export type PeerInsightQuestionKeysResponse = {
  items: PeerInsightQuestionView[];
};

export type PeerInsightQuestionView = {
  allowedInNonStrictPrivacy: boolean;
  allowedInStrictPrivacy: boolean;
  key: string;
  numeric: boolean;
  peerInsightQuestionId: bigint;
  showSuggestedAnswers: boolean;
};

export type PeerInsightTextFeedbackRequest = {
  language: Language | null;
  pageRequest: PageRequest;
  questionKey: bigint | null;
};

export type Person = {
  firstName: string;
  gender: Gender;
  lastName: string | null;
  middleName: string | null;
};

export type PortalStatusListView = {
  active: boolean;
  name: string;
};

export namespace PortalType {
  const values = ["b2bAsp", "leadPortals", "freeDownloads"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PortalType = PortalType.Impl;

export type PortletContainerAdminEditForm = {
  portletContainerId: number;
  portletIds: number[];
  showDefaults: boolean;
  status: PortletContainerStatus;
};

export type PortletContainerAdminEditView = {
  customPortlets: PortletMetadata[];
  customized: boolean;
  defaultPortlets: PortletMetadata[];
  language: "en" | "de" | "es" | "ru" | "zh" | "pt" | "fr";
  portletContainerId: number;
  showDefaults: boolean;
  status: PortletContainerStatus;
};

export namespace PortletContainerStatus {
  const values = ["DRAFT", "LIVE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PortletContainerStatus = PortletContainerStatus.Impl;

export type PortletEditView = {
  defaultFilterAllLanguages: boolean;
  embedSortingFiltering: boolean;
  itemCount: number;
  itemLayout: ItemLayout;
  loggedIn: boolean;
  loggedOut: boolean;
  mobile: boolean;
  name: string;
  portletId: number;
  portletLayout: PortletLayout;
  showTitle: boolean;
  subtitles: { additionalProperties: Record<string, string> };
  titles: { additionalProperties: Record<string, string> };
  type: PortletType;
  userGroupIds: number[];
  web: boolean;
};

export namespace PortletLayout {
  const values = ["GRID", "CAROUSEL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PortletLayout = PortletLayout.Impl;

export type PortletMetadata = {
  custom: boolean;
  defaultFilterAllLanguages: boolean;
  embedSortingFiltering: boolean;
  hideTitle: boolean;
  itemCount: number;
  itemLayout: ItemLayout;
  name: string;
  portletId: number;
  portletLayout: PortletLayout;
  subType: string | null;
  subtitle: string;
  title: string;
  type: PortletType;
};

export namespace PortletType {
  const values = [
    "IMAGETEXT",
    "SUMMARYLIST",
    "TRENDINGCHANNEL",
    "FEATUREDCHANNEL",
    "FOLLOWINGCHANNEL",
    "MYLIST",
    "PRIMARYTOPICCHANNEL",
    "CHANNELLIST",
    "ANNOTATIONLIST",
    "LGXPLIST",
    "SKETCHNOTESLGXP",
    "ACTIONABLELIST",
    "ACTIONABLELISTCUSTOM",
    "MYACTIONABLES",
    "FREESUMMARY",
    "SUMMARYLISTCUSTOM",
    "CUSTOMPAGELIST",
    "PROMOBOXCONFIG",
    "MYMIABS",
    "ASKGETABSTRACT",
    "CUSTOMIZEDMIABS",
    "MIABLIST",
    "UPCOMINGEXPERTVIDEOS",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PortletType = PortletType.Impl;

export type ProductAdminViewList = {
  list: {
    code: string;
    productId: bigint;
  }[];
};

export type PromoBoxConfigEditView = {
  customBoxes: PromoBoxEditView[];
  defaultBoxes: PromoBoxEditView[];
  defaultBoxesHidden: number[];
  showDefaultBoxes: boolean;
};

export type PromoBoxConfigForm = {
  items: PromoBoxConfigItemForm[];
  itemsHidden: number[];
  showDefaults: boolean;
};

export type PromoBoxConfigItemForm = {
  endsAt: { year: number; month: number; day: number } | null;
  promoBoxId: number;
  startsAt: { year: number; month: number; day: number } | null;
};

export type PromoBoxConfigPreview = {
  boxes: PromoBoxView[];
};

export type PromoBoxConfigPreviewFilters = {
  language: Language;
  loggedIn: string;
  platform: string;
};

export type PromoBoxEditForm = {
  image: FormFile | null;
  localizedImages: { additionalProperties: Record<string, FormFile> };
  saveReq: PromoBoxSaveRequest;
};

export type PromoBoxEditProps = {
  corporateId: number;
  languages: Language[];
  promoBoxId: number;
};

export type PromoBoxEditView = {
  backgroundColor: string | null;
  brightTextColor: boolean;
  corporateId: number;
  designApproved: boolean;
  endsAt: { year: number; month: number; day: number } | null;
  excludedLanguages: Language[];
  imageUri: string;
  localizedDescription: { additionalProperties: Record<string, string> };
  localizedImageUris: { additionalProperties: Record<string, string> };
  localizedTitle: { additionalProperties: Record<string, string> };
  localizedWebLinks: { additionalProperties: Record<string, string> };
  loggedIn: boolean;
  loggedOut: boolean;
  mobile: boolean;
  name: string;
  promoBoxId: number;
  requiredFeatures: string[];
  startsAt: { year: number; month: number; day: number } | null;
  tag: PromoBoxTag;
  web: boolean;
  webLink: string;
};

export type PromoBoxLibraryView = {
  customBoxes: PromoBoxEditView[];
  defaultBoxes: PromoBoxEditView[];
};

export type PromoBoxSaveRequest = {
  excludedLanguages: Language[];
  imageUri: string | null;
  localizedDescription: { additionalProperties: Record<string, string> };
  localizedImageUris: { additionalProperties: Record<string, string> };
  localizedTitle: { additionalProperties: Record<string, string> };
  localizedWebLinks: { additionalProperties: Record<string, string> };
  loggedIn: boolean;
  loggedOut: boolean;
  mobile: boolean;
  name: string;
  requiredFeatures: string[];
  tag: PromoBoxTag;
  web: boolean;
  webLink: string;
};

export namespace PromoBoxTag {
  const values = [
    "UNDEFINED",
    "EXPERT_WEBINAR",
    "PRO_TALK",
    "FEATURED",
    "FEATURED_SUMMARY",
    "FEATURED_ACTIONABLE",
    "FEATURED_TOPIC",
    "EMERGING_TREND",
    "INTERNATIONAL_DAY",
    "LEARNING_AT_WORK_WEEK",
    "Q_A",
    "VALUES_AND_CAPABILITIES",
    "RECOMMENDED_READS",
    "INTERNAL_THOUGHT_LEADER",
    "READING_CALENDAR",
    "NEW",
    "SPOTLIGHT",
    "PROMOTION",
    "DEAL",
    "NEWS",
    "CAMPAIGN",
    "BOOK_AWARD",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PromoBoxTag = PromoBoxTag.Impl;

export type PromoBoxView = {
  backgroundColor: string | null;
  brightTextColor: boolean;
  description: string | null;
  designApproved: boolean;
  imageUri: string;
  promoBoxId: number;
  tag: PromoBoxTag;
  title: string | null;
  webLink: string;
};

export type Province = {
  code: string;
  countryCode: string;
  provinceNames: string;
};

export type Provinces = {
  provinces: Province[];
};

export type ProximityPromptImport = {
  data: {
    dataId: number;
    proximity: {
      primaryTag: ProximityScore;
      secondaryTags: ProximityScore[];
    };
  }[];
};

export type ProximityScore = {
  name: string;
  score: number;
};

export namespace PushFrequency {
  const values = ["NONE", "DAILY", "ONCE_A_WEEK", "TWICE_A_MONTH", "ONCE_A_MONTH"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PushFrequency = PushFrequency.Impl;

export type ReferencedItemEdit = {
  refItem: ReferencedLearningItem;
  tags: Tag[];
};

export type ReferencedItemSaveRequest = {
  items: ReferencedItemEdit[];
};

export type ReferencedLearningItem = { learningItemType: "SUMMARY"; id: number } | { learningItemType: "ACTIONABLE"; id: bigint };

export type ReferencedLearningItemList = {
  items: ReferencedLearningItem[];
};

export type ReferrerProtectionForm = {
  checkReferrer: boolean;
  referrers: string[];
};

export type ReferrerProtectionFormSaveResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; form: ReferrerProtectionForm };

export type RegistrationProcessForm = {
  address1: string;
  address2: string;
  city: string;
  company: string;
  companyBilling: boolean;
  country: string;
  countryStudy: string;
  currency: string;
  department: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  paymentStatus: string;
  paymentType: string;
  postalCode: string;
  privacyPolicy: boolean;
  productId: bigint;
  promotionCode: string;
  registrationId: bigint;
  registrationUuId: string;
  state: string;
  studyField: string;
  teamOrganisation: string;
  teamUsername: string;
  telephone: string;
  termsAndConditions: boolean;
  title: boolean;
  verificationFile: string;
  verificationFileBytes: ReadonlyUint8Array;
};

export type RegistrationProcessFormAndSteps = {
  form: RegistrationProcessForm;
  steps: RegistrationProcessSteps;
};

export type RegistrationProcessProps = {
  form: RegistrationProcessForm;
  isProductFree: boolean;
  isStudent: boolean;
  myProductInformation: MyProductInformation;
  provincesCA: Provinces;
  provincesUS: Provinces;
  steps: RegistrationProcessSteps;
  studyFields: StudyFields;
};

export type RegistrationProcessStepStatus = {
  step: "REQUIRED" | "DONE" | "ACTIVE" | "UNUSED";
};

export type RegistrationProcessSteps = {
  accountForm: RegistrationProcessStepStatus;
  finishStep: RegistrationProcessStepStatus;
  paymentForm: RegistrationProcessStepStatus;
  studentForm: RegistrationProcessStepStatus;
  teamsForm: RegistrationProcessStepStatus;
};

export type RegistrationValidationResult = {
  errors: { additionalProperties: Record<string, string> };
  isValid: boolean;
  steps: RegistrationProcessSteps;
};

export type RenewSubscriptionForm = {
  ccNumber: string | null;
  ccType: string | null;
  cvv: string | null;
  existing: boolean;
  expMonth: string | null;
  expYear: string | null;
  lsvAccountNumber: string | null;
  lsvBankName: string | null;
  lsvBankTown: string | null;
  lsvClearingNumber: string | null;
  lsvFirstName: string | null;
  lsvIban: string | null;
  lsvLastName: string | null;
  manualPrice: number | null;
  paymentType: string | null;
  productId: number | null;
  promotionCode: string | null;
  street: string | null;
  subscriptionId: number;
  zip: string | null;
};

export namespace ReportFrequency {
  const values = ["ONE_TIME", "DAILY", "WEEKLY", "MONTHLY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ReportFrequency = ReportFrequency.Impl;

export type ReportListView = {
  active: boolean;
  corporateId: number;
  customerFullName: string;
  frequency: ReportFrequency;
  isRunning: boolean | null;
  name: string;
  prefixFileName: boolean;
  reportId: number;
  reportType: ReportType;
  targetExcel: boolean;
  timeFrameFrom: { year: number; month: number; day: number };
  timeFrameTo: { year: number; month: number; day: number };
  zipFile: boolean;
};

export type ReportSaveRequest = {
  active: boolean;
  activeUsersOnly: boolean;
  ccRecipients: string;
  corporateId: number;
  customerId: number;
  dayOfWeek: number;
  downloadType: DownloadType;
  frequency: ReportFrequency;
  fromDate: { year: number; month: number; day: number } | null;
  ftpFolder: string;
  ftpHost: string;
  ftpPassphrase: string;
  ftpPassword: string;
  ftpPort: number;
  ftpRemoteFileName: string;
  ftpUsername: string;
  prefixFileName: boolean;
  privateKey: string | null;
  reportId: number;
  reportName: string;
  reportType: ReportType;
  specialReport: boolean;
  targetExcel: boolean;
  toDate: { year: number; month: number; day: number } | null;
  transferProtocol: TransferProtocol;
  zipFile: boolean;
};

export namespace ReportType {
  const values = ["USERS", "ACTIVITY", "EXTENDED_USERS_EXPORT", "ACTIONABLES_ACTIVITY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ReportType = ReportType.Impl;

export type ResendConfirmationEmailForm = {
  customerName: string | null;
  subscriptionId: number;
};

export type Result = {
  completion: boolean;
  duration: string;
};

export namespace RightsProbability {
  const values = ["UNPREDICTABLE", "LIKELY", "VERY_LIKELY", "UNLIKELY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type RightsProbability = RightsProbability.Impl;

export namespace RightsState {
  const values = ["RIGHTS_NOT_REQUESTED", "PENDING", "PENDING_PUBLISHER_APPROVAL", "OK", "NO_RIGHTS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type RightsState = RightsState.Impl;

export namespace RoleResource {
  const values = [
    "ROLE_ADMIN",
    "PPS_EDITORIAL",
    "PPS_RIGHTS",
    "PPS_BOOKMARKETING",
    "PPS_TOPTENLIST",
    "PPS_WRITERREPORT",
    "PPS_STANDARDREPORT",
    "PPS_ALLSUMMARIES_LIST",
    "PPS_PUBLISHERADMIN",
    "PPS_INTERNALSEARCH",
    "ASP_ACCOUNT_ADMIN",
    "PUBLISHER_STATS_ALL",
    "ASP_STATISTICS",
    "ASP_USER_ADMIN",
    "PPS_RIGHTSADMINLIST",
    "ASP_STATISTICS_INTERNAL",
    "ADMIN_TOOL",
    "CUSTOMER_ADMIN",
    "EMAILTOOL",
    "TRANSACTION_ADMIN",
    "AFFILIATE_CLV_STATISTICS",
    "MARKETING_FILE_UPLOAD",
    "CURRENCY_EXCHANGE_RATE",
    "PPS_EDITORREPORT",
    "ADMIN_RELOAD",
    "EMAILTOOL_SNIPPETS",
    "ASP_MASSIMPORT_TOOL",
    "ASP_USER_ADMIN_LASTACCESS",
    "ASP_CENTRAL_ADMIN",
    "TXT_BUNDLE_EDIT",
    "TXT_TEXT_SINGLEEDIT",
    "TXT_TEXT_MULTIEDIT",
    "TXT_TOBEREVIEWEDLIST",
    "TXT_TOBETRANSLATEDLIST",
    "TXT_INLINE_EDIT",
    "TXT_LATEST_CHANGES",
    "TXT_SEARCH",
    "TXT_MAJOR_CHANGE",
    "TXT_ONLINE_STATE",
    "CREDIT_CARD_SETTLEMENT",
    "TXT_BUNDLE_VIEW",
    "EMAIL_LIST",
    "SOCIAL_COMMENT_FLAG",
    "CUSTOMER_WRITER",
    "RECOMMENDER_EXPORT",
    "KREC_EDIT",
    "WEB_RIGHTS_REQUEST",
    "SCORM_PACKAGE",
    "PPS_VIEW",
    "AFFILIATE_ADMIN",
    "SUMMARY_QR_CODE",
    "MARC_XML",
    "REGENERATE_SUMMARY_FILES",
    "HR_ADMIN_TOOL",
    "SUMMARY_STATISTICS",
    "CF_SUBSCRIPTIONS",
    "ASP_SSO_DOMAIN",
    "CUSTOMER_ADMIN_SUBSCRIPTION",
    "CUSTOMER_ADMIN_FINANCE",
    "OAUTH2_CLIENT_ADMIN",
    "ASP_USER_GROUP_ADMIN",
    "TOP_DOWNLOADS_TOOL",
    "WRITER_PORTAL",
    "ASP_LOGO_UPLOAD",
    "PORTLET_ADMIN",
    "PORTLET_SUPER_ADMIN",
    "FREEABSTRACT_ADMIN_TOOL",
    "PROMOBOXES_ADMIN",
    "CORPORATE_CONTACT",
    "CORPORATE_COMMENTS",
    "PPS_NEW_CONTENT_INPUT",
    "PPS_NEW_CONTENT_INPUT_ADMIN",
    "ABSENCES_ADMIN",
    "CHANNEL_ADMIN",
    "PROMOBOXES",
    "SSO_DOMAINS",
    "PPS_PORTAL",
    "PPS_PORTAL_ADMIN",
    "CHANNEL_LIST",
    "B2B_STATISTICS",
    "LIBRARY_CONNECTOR",
    "CHANNEL_ADMIN_GLOBAL",
    "PPS_FREELANCER_CONTACT_TOOL",
    "PPS_API_PRODUCTION_POINTS",
    "PPS_API_PRODUCTION_POINTS_ADMIN",
    "PPS_API_WRITING_OFFER",
    "PPS_API_EDITING_OFFER",
    "PPS_FLOW",
    "PPS_FLOW_ADMIN",
    "PPS_API_JOB",
    "PPS_API_INVOICE",
    "SCIM_TOKEN_VIEWER",
    "PPS_API_OFFER",
    "PRODUCTION_REPORT",
    "PPS_RIGHTS_FLOW",
    "PPS_AUTHOR_TOOL",
    "GA_KNOWLEDGE_BASE",
    "GA_SERVICE_DESK",
    "PPS_KNOWLEDGE_BASE",
    "EDITORIAL_KNOWLEDGE_BASE",
    "LGXP_ADMIN",
    "PPS_RIGHTS_TEAM_REPORTS",
    "LGXP_GLOBAL_ADMIN",
    "PPS_EDITORIAL_TEAM_REPORTS",
    "PPS_API_SUMMARY_CREATION",
    "CUSTOMER_STORY_ADMIN",
    "PROMOTION_CODE_TOOL",
    "PPS_GLOBAL_MOST_WANTED_SUMMARY_REPORT",
    "ASP_LIST",
    "PPS_USER_API",
    "ASP_SENSITIVE_CONTENT",
    "CHANNEL_ADMIN_SEARCH",
    "PPS_TEAM_MOST_WANTED_EDITORIAL_SUMMARY_REPORT",
    "PPS_PERSONAL_MOST_WANTED_EDITORIAL_SUMMARY_REPORT",
    "PPS_TEAM_MOST_WANTED_RIGHTS_SUMMARY_REPORT",
    "PPS_PERSONAL_MOST_WANTED_RIGHTS_SUMMARY_REPORT",
    "ACTIONABLE_ADMIN",
    "PPS_COMPONENT_DEMO",
    "ASP_MASSIMPORT_TOOL_EXTENDED",
    "ASP_UNIDENTIFIED_USER_ADMIN",
    "PPS_PAYMENT_TOOL",
    "HR_ADMIN_FULL_ACCESS",
    "PPS_ORIGINAL_SUMMARY_FILE_ACCESS",
    "CUSTOMER_REPORTS",
    "PPS_ARCHIVING_SUMMARY",
    "LGXP_ADMIN_SEARCH",
    "PPS_USER_BETA_TEST",
    "ACTIONABLE_ADMIN_SEARCH",
    "CUSTOM_PAGE_ADMIN",
    "PPS_EXPENSE_TOOL",
    "VERIFY_INPUT_TOOL",
    "VERIFY_PUBLISH",
    "VERIFY_UPLOAD",
    "VERIFY_DASHBOARD",
    "CSM_ADVANCED_SEARCH",
    "VERIFY_VERIFICATION",
    "VERIFY_EDITING",
    "SUMMARIES_BULK_LOAD",
    "ACTIONABLES_BULK_LOAD",
    "TAG_ADMIN",
    "EXPERTVIDEO_ADMIN",
    "MIAB_ADMIN",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type RoleResource = RoleResource.Impl;

export type RoleResourceConstraint = {
  corporateIds: number[];
  customerId: number;
  languages: Language[];
  resource: RoleResource;
};

export type SearchFiltersForm = {
  actionableLanguageFormFilter: ActionableFiltersForm;
  channelPage: number;
  query: string;
  summariesPage: number;
  summarySearchFilters: SummaryFiltersForm;
};

export type SensitiveContentListViewList = {
  list: {
    active: boolean;
    coverUri: string;
    id: bigint;
    title: string;
  }[];
};

export type ShareContent = { shareType: "CONTENT"; metadata: ContentMetadata } | { shareType: "CHANNEL"; channel: ChannelMetadata };

export type ShareEvent = {
  contentItemType: ContentItemType;
  id: string;
  shareMedium: ShareMedium;
};

export namespace ShareMedium {
  const values = ["COPY_LINK", "FACEBOOK", "FACEBOOK_WORKSPACE", "TWITTER", "LINKEDIN", "EMAIL", "IFRAME", "SCORM", "TEAMS", "WECHAT", "XING"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ShareMedium = ShareMedium.Impl;

export type ShareProps = {
  content: ShareContent;
  shareOptions: ShareMedium[];
};

export type SkillGroups = {
  skillGroup: {
    competencies: {
      item: SkillOverviewItem;
      skills: {
        item: SkillOverviewItem;
      }[];
    }[];
    item: SkillOverviewItem;
  }[];
};

export type SkillOverviewItem = {
  channelId: bigint;
  coverUri: string;
  title: string;
  url: string;
};

export type SkillPageContentProps = {
  content: ContentMetadataView;
  hasActionableFeature: boolean;
  tag: Tag;
  tracking: {
    actionableParams: string;
    miabParams: string;
    summaryParams: string;
  };
};

export type SkillPromptAnswer = {
  primaryTag: string;
  secondaryTags: string[];
};

export type SkillProximityPromptAnswer = {
  primaryTag: ProximityScore;
  secondaryTags: ProximityScore[];
};

export type SkillTaxonomy = {
  skills: {
    sub_skills: {
      sub_skills: {
        title: string;
        title_DE: string | null;
        title_EN: string | null;
        title_ES: string | null;
      }[];
      title: string;
      title_DE: string | null;
      title_EN: string | null;
      title_ES: string | null;
    }[];
    title: string;
    title_DE: string | null;
    title_EN: string | null;
    title_ES: string | null;
  }[];
};

export namespace StudentIdVerificationProvider {
  const values = ["UNIDAYS", "UNIDAYS_PERK", "SHEERID", "MANUAL", "UNKNOWN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type StudentIdVerificationProvider = StudentIdVerificationProvider.Impl;

export namespace StudentVerificationStatus {
  const values = ["VERIFIED", "EXPIRED", "FAILED", "IN_PROGRESS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type StudentVerificationStatus = StudentVerificationStatus.Impl;

export type StudentVerificationView = {
  countryOfStudy: string;
  createdAt: DateTime<true>;
  fieldOfStudy: string | null;
  proof: string | null;
  provider: StudentIdVerificationProvider;
  status: StudentVerificationStatus;
  subscriptionId: number | null;
  verifiedAt: DateTime<true> | null;
};

export type StudyField = {
  defined: boolean;
  value: string;
};

export type StudyFields = {
  studyFields: StudyField[];
};

export type SubscriptionAdminForm = {
  extraMonths: number | null;
  isPaid: boolean;
  productId: number;
  reference: string | null;
  showCommentModal: boolean;
  status: SubscriptionStatus;
  studentVerificationCountryOfStudy: string | null;
  studentVerificationFieldOfStudy: string | null;
  studentVerificationProof: string | null;
  studentVerificationProvider: string | null;
  studentVerificationStatus: string | null;
  studentVerificationVerifiedAt: DateTime<true> | null;
  subscriptionEndDate: { year: number; month: number; day: number };
  subscriptionId: number;
  subscriptionStartDate: { year: number; month: number; day: number };
  transactions: SubscriptionTransaction[];
};

export type SubscriptionAdminView = {
  affiliate: string | null;
  cancelLinkForCustomerUri: string;
  comment: string | null;
  customerId: number;
  emailAddressForCancelConfirmation: string;
  extraMonths: number | null;
  giftCertificateUri: string | null;
  giftCodesAvailable: GiftCodeStatus;
  invoicesAndReminders: Invoice[] | null;
  isGiftSubscription: boolean;
  isPaid: boolean;
  motivationToBuy: string | null;
  productId: number;
  productName: string;
  promotionCode: string | null;
  reference: string;
  referredBy: string | null;
  renewSource: SubscriptionRenewSource;
  status: SubscriptionStatus;
  studentVerification: StudentVerificationView | null;
  subscriptionEndDate: { year: number; month: number; day: number };
  subscriptionId: number;
  subscriptionRenewInfo: SubscriptionRenewInfoView | null;
  subscriptionStartDate: { year: number; month: number; day: number };
  transactions: SubscriptionTransaction[];
};

export type SubscriptionCreditForm = {
  amountToCredit: number;
  balance: number;
  comment: string | null;
  sendEmail: boolean;
  stopSubscription: boolean;
  subscriptionId: number;
  transactionTypeId: number;
};

export type SubscriptionCreditView = {
  amountToCredit: number;
  balance: number;
  currencyCode: string | null;
  customerId: number;
  emailSubject: string | null;
  emailText: string | null;
  isEmailHtml: boolean | null;
  isGiftSubscription: boolean;
  isSubscriptionStopped: boolean;
  subscriptionId: number;
  transaction: SubscriptionTransaction;
};

export type SubscriptionPayment = {
  balance: MonetaryAmount;
  isMobilePayment: boolean;
  paymentType: string | null;
};

export type SubscriptionRenewInfoView = {
  ccNumber: string | null;
  ccType: CreditCardType | null;
  cvv: string | null;
  expMonth: string | null;
  expYear: string | null;
  lsvAccountNumber: string | null;
  lsvBankIban: string | null;
  lsvBankName: string | null;
  lsvBankTown: string | null;
  lsvClearingNumber: string | null;
  lsvFirstName: string | null;
  lsvLastName: string | null;
  manualPrice: number | null;
  paymentType: string | null;
  productId: number | null;
  promotionCode: string | null;
  street: string | null;
  subscriptionId: number;
  zip: string | null;
};

export namespace SubscriptionRenewSource {
  const values = ["NEW", "AUTOMATIC", "MANUAL", "UPGRADE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SubscriptionRenewSource = SubscriptionRenewSource.Impl;

export namespace SubscriptionStatus {
  const values = ["ACTIVE", "STOP", "CANCEL", "RENEWED", "RENEWALFAILED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SubscriptionStatus = SubscriptionStatus.Impl;

export type SubscriptionTransaction = {
  amount: DisplayMonetaryAmount;
  comment: string | null;
  customerId: number;
  generatedAt: { year: number; month: number; day: number };
  paymentInfoAlipay: AlipayPaymentView | null;
  paymentInfoAndroid: AndroidPaymentView | null;
  paymentInfoCC: CreditCardPaymentView | null;
  paymentInfoIOS: IosPaymentView | null;
  paymentInfoInvoice: InvoicePaymentView | null;
  paymentInfoLSV: LsvPaymentView | null;
  paymentInfoMiles: MilesPaymentView | null;
  paymentInfoPaypal: PayPalPaymentView | null;
  paymentReceiptUri: string;
  paymentType: PaymentType;
  quantity: number;
  subscriptionId: number;
  transactionId: number;
  transactionType: "DEBIT" | "CREDIT";
};

export type SuggestedAnswerView = {
  actionableId: bigint;
  description: string;
  peerInsightQuestionId: bigint;
  suggestedAnswerId: bigint | null;
  title: string;
};

export type SummaryActivityReportView = {
  customer: CustomerReportView | null;
  downloadCountry: string | null;
  downloadFormat: string | null;
  primarySkills: string[];
  skills: string[];
  summary: ContentMetadata_SUMMARY;
  timeStamp: DateTime<true>;
};

export type SummaryBulkLoadForm = {
  corporateId: number | null;
  dataIds: number[];
  includeInactive: boolean;
};

export type SummaryCardDemoProps = {
  summaryList: ContentMetadata_SUMMARY[];
};

export type SummaryContent = {
  aboutAuthorHtml: string | null;
  aboutTextHtml: string | null;
  abstractTextHtml: string | null;
  bookId: number;
  dataId: number;
  historicalBackgroundHtml: string | null;
  introHtml: string | null;
  longReviewHtml: string | null;
  outputFormat: SummaryOutputFormat;
  reviewLeadHtml: string | null;
  reviewTitle: string | null;
  sourceIsbn: string | null;
  sourcePages: number;
  sourceUrl: string | null;
  takeaways: string[];
};

export type SummaryCoverInformation = {
  imageUri: string;
  summarySourceType: "BOOK" | "VIDEO" | "ARTICLE" | "CHAPTER" | "REPORT" | "PODCAST";
  title: string | null;
};

export namespace SummaryDocumentType {
  const values = ["ABSTRACT", "REVIEW"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummaryDocumentType = SummaryDocumentType.Impl;

export namespace SummaryFileFormat {
  const values = ["PDF", "EPUB", "AUDIO"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummaryFileFormat = SummaryFileFormat.Impl;

export type SummaryFiltersForm = {
  audioFormFilter: boolean;
  languageFormFilter: Language[];
  miabFormFilter: boolean;
  publicationDateFormFilter: number[];
  qualityFormFilter: string[];
  ratingFormFilter: number[];
  sourceFormFilter: string[];
};

export type ContentId_SUMMARY = {
  contentType: "SUMMARY";
  id: bigint;
};

export type SummaryIdsList = {
  summaryIds: number[];
};

export type SummaryListPortletView = {
  items: ContentMetadata_SUMMARY[];
  metadata: PortletMetadata;
  pageInfo: PageInfo;
  viewAllUri: string | null;
};

export type SummaryListView = {
  authors: string;
  dataId: number;
  language: "en" | "de" | "es" | "ru" | "zh" | "pt" | "fr";
  source: string;
  title: string;
};

export type ContentMetadata_SUMMARY = {
  activatedAt: DateTime<true> | null;
  activationTime: DateTime<true> | null;
  active: boolean;
  audioActivatedAt: DateTime<true> | null;
  audioActive: boolean;
  audioDuration: number;
  authorInfo: string | null;
  authors: AuthorListView[];
  backgroundColor: string | null;
  bestseller: boolean;
  businessClassic: boolean;
  canonicalName: string;
  common: ContentMetadataCommon;
  contentId: ContentId_SUMMARY;
  contentItemId: bigint;
  contentItemType: ContentItemType;
  contentType: "SUMMARY";
  countriesExcluded: Country[];
  countriesIncluded: Country[];
  coverModifiedAt: DateTime<true> | null;
  coverUri: string;
  dataId: number;
  descriptionHtml: string | null;
  documentType: SummaryDocumentType;
  duration: number;
  imageBorder: boolean;
  language: Language;
  miabId: bigint | null;
  modifiedAt: DateTime<true> | null;
  originalTitle: string | null;
  previewAvailable: boolean;
  publicationDate: string | null;
  publisherInfo: string | null;
  publisherName: string | null;
  rating: number | null;
  sourceType: SummarySourceType;
  subtitle: string | null;
  summaryPages: number;
  summaryWords: number;
  tags: Tag[];
  teaser: string | null;
  title: string | null;
};

export type SummaryMetadataArray = {
  items: ContentMetadata_SUMMARY[];
};

export namespace SummaryOutputFormat {
  const values = ["TWO_PAGES", "FIVE_PAGES", "EIGHT_PAGES"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummaryOutputFormat = SummaryOutputFormat.Impl;

export type SummaryPageProps = {
  content: SummaryContent;
  keyInsights: string[];
  metadata: ContentMetadata_SUMMARY;
  qualities: string[];
  summaryFileFormats: SummaryFileFormat[];
  summaryPageStats: SummaryPageStats;
};

export type SummaryPageStats = {
  amountComments: bigint | null;
  amountCompletions: bigint | null;
};

export namespace SummarySort {
  const values = ["ACTIVATED_AT", "POPULARITY", "TITLE", "PUBLICATION_DATE", "RATING", "RELEVANCE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummarySort = SummarySort.Impl;

export namespace SummarySourceType {
  const values = ["BOOK", "VIDEO", "ARTICLE", "CHAPTER", "REPORT", "PODCAST"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummarySourceType = SummarySourceType.Impl;

export type Tag =
  | { discriminator: "dynamicString"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindDynamicString; value: string }
  | { discriminator: "dynamicBoolean"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindDynamicBoolean; value: boolean }
  | { discriminator: "dynamicNumber"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindDynamicNumber; value: number }
  | { discriminator: "dynamicVoid"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindDynamicVoid }
  | { discriminator: "channel"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindChannel; value: string }
  | { discriminator: "primary-skill"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindPrimarySkill; value: string }
  | { discriminator: "skill"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindSkill; value: string }
  | { discriminator: "key-quality"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindKeyQuality; value: string }
  | { discriminator: "classification"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindClassification; value: string };

export type TagAdminFilter = {
  query: string;
  tagKinds: TagKind[];
};

export type TagImport = {
  data: {
    dataId: number;
    primary_skill: string;
    secondary_skills: string[];
  }[];
};

export type TagKind =
  | { discriminator: "classification"; id: bigint; kind: KindClassification }
  | { discriminator: "channel"; id: bigint; kind: KindChannel }
  | { discriminator: "primary-skill"; id: bigint; kind: KindPrimarySkill }
  | { discriminator: "skill"; id: bigint; kind: KindSkill }
  | { discriminator: "key-quality"; id: bigint; kind: KindKeyQuality }
  | { discriminator: "dynamicVoid"; id: bigint; kind: string }
  | { discriminator: "dynamicString"; id: bigint; kind: string }
  | { discriminator: "dynamicBoolean"; id: bigint; kind: string }
  | { discriminator: "dynamicNumber"; id: bigint; kind: string };

export type TagKindChannel = {
  discriminator: KindChannel;
  id: bigint;
  kind: KindChannel;
};

export type TagKindClassification = {
  discriminator: KindClassification;
  id: bigint;
  kind: KindClassification;
};

export type TagKindDynamicBoolean = {
  discriminator: KindDynamicBoolean;
  id: bigint;
  kind: string;
};

export type TagKindDynamicNumber = {
  discriminator: KindDynamicNumber;
  id: bigint;
  kind: string;
};

export type TagKindDynamicString = {
  discriminator: KindDynamicString;
  id: bigint;
  kind: string;
};

export type TagKindDynamicVoid = {
  discriminator: KindDynamicVoid;
  id: bigint;
  kind: string;
};

export type TagKindKeyQuality = {
  discriminator: KindKeyQuality;
  id: bigint;
  kind: KindKeyQuality;
};

export type TagKindPrimarySkill = {
  discriminator: KindPrimarySkill;
  id: bigint;
  kind: KindPrimarySkill;
};

export type TagKindSkill = {
  discriminator: KindSkill;
  id: bigint;
  kind: KindSkill;
};

export namespace TagSourceType {
  const values = ["USER", "AI"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type TagSourceType = TagSourceType.Impl;

export type TagValidationResponse = { status: "success" } | { status: "error"; message: string };

export type TaggedItem = {
  item: ReferencedLearningItem;
  tag: Tag;
};

export type TeamSubscription = {
  teamPortalCorporateId: number;
  teamPortalUrl: string;
  teamPortalUsername: string;
};

export type ToastState = {
  message: string;
  title: string | null;
  uuid: string;
  variant: ToastVariant;
};

export namespace ToastVariant {
  const values = ["primary", "success", "warning", "danger"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ToastVariant = ToastVariant.Impl;

export namespace TransferProtocol {
  const values = ["FTP", "SFTP", "FTPS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type TransferProtocol = TransferProtocol.Impl;

export type Translations = {
  keys: { additionalProperties: Record<string, string> };
};

export type UserContext = {
  loggedIn: boolean;
  u: string | null;
};

export type UserListView = {
  customerId: number;
  email: string;
  gender: number;
  name: string;
};

export type UserReportView = {
  customer: CustomerReportView;
  lastMobileUsage: DateTime<true> | null;
  lastWebUsage: DateTime<true> | null;
  pull: number;
  push: number;
};

export type ValidationErrorReasons = {
  fields: { additionalProperties: Record<string, string[]> };
  general: string[];
};

export type Verb = {
  display: { additionalProperties: Record<string, string> };
  id: string;
};

export type VerifaiActionResult = { kind: "NOT_FOUND_ERROR" } | { kind: "IDENTITY_NOT_CONFIRMED_ERROR" } | { kind: "SUCCESS" };

export type VerifaiAutofillRequest = {
  model: VerifaiOpenAiModels;
  prompt: string | null;
  temperature: number | null;
};

export type VerifaiAutofillResult = { kind: "NOT_ASSIGNED_ERROR" } | { kind: "EMPTY_TEXT_ERROR" } | { kind: "SUCCESS"; data: VerifaiContentForm };

export type VerifaiCardView = {
  authorRating: VerifaiRating;
  contentRating: VerifaiRating;
  creator: string;
  duration: number;
  formatType: VerifaiFormatType;
  keywords: string[];
  site: string;
  sourceRating: VerifaiRating;
  title: string;
  verifaiContentId: bigint;
};

export type VerifaiClaimItemsRequest = {
  verifaiContentIds: bigint[];
};

export type VerifaiContentForm = {
  contentStatus: VerifaiContentStatus;
  contentSummary: string;
  editorCustomerId: number | null;
  evergreen: boolean;
  formatType: VerifaiFormatType;
  hashId: string;
  invoiced: boolean;
  keywords: string;
  originalText: string;
  parentalCrudeLanguage: boolean;
  parentalPolitical: boolean;
  parentalReligious: boolean;
  productionRemarks: string;
  profileIds: bigint[];
  publicationDate: { year: number; month: number; day: number };
  rating: VerifaiRating;
  ratingComment: string | null;
  ratingInnovation: VerifaiRating;
  ratingRelevance: VerifaiRating;
  ratingReliability: VerifaiRating;
  ratingStyle: VerifaiRating;
  readingTime: number;
  resourceUrl: string;
  rewrittenText: string;
  subtitle: string | null;
  synopsis: string;
  title: string;
  trustMarkerIds: bigint[];
  uploaderCustomerId: number;
  useAiAssistant: boolean;
  verifaiContentId: bigint | null;
  verifaiCreatorIds: bigint[];
  verifaiSiteId: bigint;
  verifierCustomerId: number | null;
  whatYouWillLearn: string;
};

export type VerifaiContentFormResult =
  | { kind: "INVALID_URL_ERROR" }
  | { kind: "DUPLICATE_URL_ERROR" }
  | { kind: "ID_MISMATCH_ERROR" }
  | { kind: "NOT_ASSIGNED_ERROR" }
  | { kind: "RATING_MISMATCH_ERROR" }
  | { kind: "DATABASE_ERROR" }
  | { kind: "SUCCESS"; data: VerifaiContentForm };

export namespace VerifaiContentStatus {
  const values = ["PREPROCESSING", "OPEN", "ASSIGNED", "SUBMITTED", "EDITED", "REJECTED", "ARCHIVED", "PUBLISHED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type VerifaiContentStatus = VerifaiContentStatus.Impl;

export type VerifaiCreator = {
  firstName: string;
  lastName: string;
  linkedInUrl: string | null;
  middleName: string;
  rating: VerifaiRating;
  trustMarkers: VerifaiTrustMarker[];
  verifaiCreatorId: bigint;
};

export type VerifaiCreatorFilters = {
  contacted: boolean | null;
  firstName: string | null;
  lastName: string | null;
  linkedInProfileUrl: string | null;
  middleName: string | null;
  minPublishedItems: number | null;
  rating: string | null;
  verifaiCreatorIds: bigint[] | null;
};

export type VerifaiCreatorForm = {
  biography: string;
  firstName: string | null;
  gender: VerifaiCreatorGender;
  hashId: string;
  lastName: string;
  linkedInProfileUrl: string;
  middleName: string | null;
  rating: VerifaiRating;
  trustMarkerIds: bigint[];
  verifaiCreatorId: bigint | null;
  verifierCustomerId: number | null;
};

export type VerifaiCreatorFormResult = { kind: "ID_MISMATCH_ERROR" } | { kind: "SUCCESS"; data: VerifaiCreatorForm };

export namespace VerifaiCreatorGender {
  const values = ["MALE", "FEMALE", "UNKNOWN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type VerifaiCreatorGender = VerifaiCreatorGender.Impl;

export type VerifaiCreatorOverviewItem = {
  biography: string;
  contacted: boolean;
  dashboardUrl: string;
  firstName: string;
  lastName: string;
  linkedInProfileUrl: string;
  middleName: string;
  numberPublishedItems: number;
  rating: VerifaiRating;
  verifaiCreatorId: bigint;
};

export type VerifaiCreatorPageProps = {
  hashId: string;
};

export type VerifaiCreatorSearch = {
  search: string;
};

export type VerifaiCreatorView = {
  authorName: string;
  claimableItems: VerifaiOverviewItem[];
  claimedItems: VerifaiOverviewItem[];
};

export type VerifaiCreatorViewResult = { kind: "NOT_FOUND_ERROR" } | { kind: "IDENTITY_NOT_CONFIRMED_ERROR" } | { kind: "SUCCESS"; data: VerifaiCreatorView };

export type VerifaiDetailViewItem = {
  coverUri: string;
  evergreen: boolean;
  formatColorBase: string;
  formatColorDark: string;
  formatColorLight: string;
  formatType: VerifaiFormatType;
  hashId: string;
  keywords: string[];
  originalText: string | null;
  profiles: VerifaiProfile[];
  publicationDate: { year: number; month: number; day: number };
  rating: VerifaiRating;
  readingTime: number;
  resourceUrl: string;
  rewrittenText: string | null;
  subtitle: string | null;
  summary: string;
  synopsis: string;
  title: string;
  trustMarkers: VerifaiTrustMarker[];
  verifaiContentCreators: VerifaiCreator[];
  verifaiContentId: bigint | null;
  verifaiSite: VerifaiSite | null;
  whatYouWillLearn: string[];
};

export type VerifaiDetailViewTranslations = {
  actions: string;
  authorRating: string;
  authors: string;
  content: string;
  contentRating: string;
  cta: string;
  export: string;
  format: string;
  jobProfiles: string;
  languages: VerifaiLanguageType[];
  minutes: string;
  notAvailable: string;
  ratingDetails: string;
  source: string;
  sourceRating: string;
  summary: string;
  synopsis: string;
  takeaways: string;
};

export namespace VerifaiFormatType {
  const values = ["ARTICLE", "BLOGPOST", "PODCAST", "REPORT", "VIDEO"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type VerifaiFormatType = VerifaiFormatType.Impl;

export type VerifaiLanguageType = {
  isoCode: string;
  name: string;
};

export type VerifaiNavLink = {
  ico: string | null;
  name: string;
  url: string;
};

export type VerifaiNavigationProps = {
  navLinks: VerifaiNavLink[];
};

export namespace VerifaiOpenAiModels {
  const values = ["O3_MINI_2025_01_31", "O4_MINI_2025_04_16", "GPT_41_2025_04_14"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type VerifaiOpenAiModels = VerifaiOpenAiModels.Impl;

export type VerifaiOverviewFilters = {
  contentStatus: VerifaiContentStatus | null;
  editor: string | null;
  formatType: string | null;
  invoiced: boolean | null;
  publisher: string | null;
  rating: string | null;
  resourceUrl: string | null;
  title: string | null;
  uploader: string | null;
  verifaiContentId: bigint | null;
  verifier: string | null;
};

export type VerifaiOverviewItem = {
  contentStatus: VerifaiContentStatus;
  editor: string;
  formatType: VerifaiFormatType;
  hashId: string;
  invoiced: boolean;
  publisher: string;
  rating: VerifaiRating;
  resourceUrl: string;
  title: string;
  uploader: string;
  verifaiContentId: bigint;
  verifier: string;
};

export type VerifaiOverviewItemList = {
  items: VerifaiOverviewItem[];
};

export type VerifaiProfile = {
  profile: string;
  verifaiProfileId: bigint;
};

export namespace VerifaiRating {
  const values = ["A", "B", "C", "F"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type VerifaiRating = VerifaiRating.Impl;

export type VerifaiRedirectItem = {
  resourceUrl: string;
  verifaiContentId: bigint | null;
};

export type VerifaiSite = {
  rating: VerifaiRating;
  siteUrl: string | null;
  title: string | null;
  trustMarkers: VerifaiTrustMarker[] | null;
  verifaiSiteId: bigint | null;
};

export type VerifaiSiteForm = {
  rating: VerifaiRating;
  siteUrl: string;
  title: string;
  trustMarkerIds: bigint[];
  verifaiSiteId: bigint | null;
  verifierCustomerId: number | null;
};

export type VerifaiSiteFormList = {
  items: VerifaiSiteForm[];
};

export type VerifaiSiteFormResult = { kind: "ID_MISMATCH_ERROR" } | { kind: "SUCCESS"; data: VerifaiSiteForm };

export type VerifaiTrustMarker = {
  label: string;
  verifaiTrustMarkerId: bigint;
};

export namespace VerifaiTrustMarkerType {
  const values = ["CONTENT_RELIABILITY", "CONTENT_RELEVANCE", "CONTENT_INNOVATION", "CONTENT_STYLE", "SITE", "CREATOR"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type VerifaiTrustMarkerType = VerifaiTrustMarkerType.Impl;

export type VerifaiTrustMarkerView = {
  applicableRating: VerifaiRating;
  label: string;
  trustMarkerType: VerifaiTrustMarkerType;
  verifaiTrustMarkerId: bigint;
};

export type VerifaiTrustMarkerViewList = {
  items: VerifaiTrustMarkerView[];
};

export type ContentId_VERIFY = {
  contentType: "VERIFY";
  id: bigint;
};

export type ContentMetadata_VERIFY = {
  authors: string[];
  common: ContentMetadataCommon;
  content: string;
  contentId: ContentId_VERIFY;
  contentType: "VERIFY";
  formatType: string;
  jobProfiles: string;
  keywords: string;
  modifiedAt: DateTime<true> | null;
  publicationDate: string;
  readingTime: string;
  resourceUrl: string;
  source: string;
  subtitle: string;
  summary: string;
  synopsis: string;
  takeaways: string;
};

export type VueDemoProps = {
  language: Language;
  subtitle: string;
};

export type WarmupEitherEditForm_B2B = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  extraField1: string;
  extraField2: string;
  extraField3: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  kind: "B2B";
  language: Language;
  lastName: string;
  password: string;
  termsAndConditions: boolean;
};

export type WarmupEitherValidatedForm_B2B = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  extraField1: string | null;
  extraField2: string | null;
  extraField3: string | null;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  kind: "B2B";
  language: Language;
  lastName: string;
  password: string;
  termsAndConditions: boolean;
};

export type WarmupB2bValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupEitherValidatedForm_B2B };

export type WarmupEitherEditForm_B2C = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  kind: "B2C";
  language: Language;
  lastName: string;
  password: string;
  termsAndConditions: boolean;
};

export type WarmupEitherValidatedForm_B2C = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  kind: "B2C";
  language: Language;
  lastName: string;
  password: string;
  termsAndConditions: boolean;
};

export type WarmupB2cValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupEitherValidatedForm_B2C };

export type WarmupEditForm = {
  either: WarmupEitherEditForm;
};

export type WarmupEitherEditForm =
  | {
      kind: "SSO";
      corporateId: bigint;
      countryCode: string;
      email: string;
      evrUuid: string;
      extraField1: string;
      extraField2: string;
      extraField3: string;
      firstName: string;
      frequency: WeeklyEmailPushFrequency;
      function: string | null;
      language: Language;
      lastName: string;
      password: string;
      ssoNameId: string;
      termsAndConditions: boolean;
      title: bigint | null;
      town: string | null;
    }
  | {
      kind: "B2B";
      corporateId: bigint;
      countryCode: string;
      email: string;
      evrUuid: string;
      extraField1: string;
      extraField2: string;
      extraField3: string;
      firstName: string;
      frequency: WeeklyEmailPushFrequency;
      language: Language;
      lastName: string;
      password: string;
      termsAndConditions: boolean;
    }
  | { kind: "TRIAL"; corporateId: bigint; countryCode: string; email: string; evrUuid: string; firstName: string; frequency: WeeklyEmailPushFrequency; language: Language; lastName: string; password: string; termsAndConditions: boolean }
  | { kind: "B2C"; corporateId: bigint; countryCode: string; email: string; evrUuid: string; firstName: string; frequency: WeeklyEmailPushFrequency; language: Language; lastName: string; password: string; termsAndConditions: boolean };

export type WarmupEitherValidatedForm =
  | {
      kind: "SSO";
      corporateId: bigint;
      countryCode: string;
      email: string;
      evrUuid: string;
      extraField1: string | null;
      extraField2: string | null;
      extraField3: string | null;
      firstName: string;
      frequency: WeeklyEmailPushFrequency;
      function: string | null;
      language: Language;
      lastName: string;
      password: string;
      ssoNameId: string;
      termsAndConditions: boolean;
      title: bigint | null;
      town: string | null;
    }
  | {
      kind: "B2B";
      corporateId: bigint;
      countryCode: string;
      email: string;
      evrUuid: string;
      extraField1: string | null;
      extraField2: string | null;
      extraField3: string | null;
      firstName: string;
      frequency: WeeklyEmailPushFrequency;
      language: Language;
      lastName: string;
      password: string;
      termsAndConditions: boolean;
    }
  | { kind: "TRIAL"; corporateId: bigint; countryCode: string; email: string; evrUuid: string; firstName: string; frequency: WeeklyEmailPushFrequency; language: Language; lastName: string; password: string; termsAndConditions: boolean }
  | { kind: "B2C"; corporateId: bigint; countryCode: string; email: string; evrUuid: string; firstName: string; frequency: WeeklyEmailPushFrequency; language: Language; lastName: string; password: string; termsAndConditions: boolean };

export type WarmupEitherValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupEitherValidatedForm };

export type WarmupExtraField =
  | { kind: "OPTION"; mandatory: boolean; options: { additionalProperties: Record<string, string> }; title: string; value: string | null }
  | { kind: "TEXT"; mandatory: boolean; title: string; value: string | null };

export type WarmupExtraFieldWrapper = {
  extraField: WarmupExtraField;
};

export type WarmupRelayType = { kind: "DEFAULT"; relayState: string } | { kind: "CONTENT"; contentMetadata: ContentMetadata; relayState: string };

export type WarmupEitherEditForm_SSO = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  extraField1: string;
  extraField2: string;
  extraField3: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  function: string | null;
  kind: "SSO";
  language: Language;
  lastName: string;
  password: string;
  ssoNameId: string;
  termsAndConditions: boolean;
  title: bigint | null;
  town: string | null;
};

export type WarmupEitherValidatedForm_SSO = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  extraField1: string | null;
  extraField2: string | null;
  extraField3: string | null;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  function: string | null;
  kind: "SSO";
  language: Language;
  lastName: string;
  password: string;
  ssoNameId: string;
  termsAndConditions: boolean;
  title: bigint | null;
  town: string | null;
};

export type WarmupSsoValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupEitherValidatedForm_SSO };

export type WarmupEitherEditForm_TRIAL = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  kind: "TRIAL";
  language: Language;
  lastName: string;
  password: string;
  termsAndConditions: boolean;
};

export type WarmupEitherValidatedForm_TRIAL = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  kind: "TRIAL";
  language: Language;
  lastName: string;
  password: string;
  termsAndConditions: boolean;
};

export type WarmupTrialValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupEitherValidatedForm_TRIAL };

export type WarmupValidatedForm = {
  either: WarmupEitherValidatedForm;
};

export type WarmupValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupValidatedForm };

export type WarmupWidgetProps = {
  corporationName: string;
  customTermsHtml: string | null;
  extraField1: WarmupExtraFieldWrapper | null;
  extraField2: WarmupExtraFieldWrapper | null;
  extraField3: WarmupExtraFieldWrapper | null;
  form: WarmupEditForm;
  relayType: WarmupRelayType;
};

export namespace WeeklyEmailPushFrequency {
  const values = ["NONE", "ONCE_A_WEEK", "TWICE_A_MONTH", "ONCE_A_MONTH"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type WeeklyEmailPushFrequency = WeeklyEmailPushFrequency.Impl;

export type WishlistManagementData = {
  editorOptions: DropdownEntry[];
  list: WishlistManagementItemList;
};

export type WishlistManagementEditorUpdate = {
  managingEditorCustomerId: number | null;
};

export type WishlistManagementItem = {
  coverUri: string | null;
  dataId: number | null;
  description: string;
  detailInfo: string;
  isbn: string | null;
  krecId: number;
  language: Language;
  managingEditorCustomerId: number | null;
  rightsGranted: boolean;
  suggestedAt: DateTime<true>;
  tinyUrl: string | null;
  title: string;
};

export type WishlistManagementItemList = {
  items: WishlistManagementItem[];
};

export type WishlistManagementLanguageUpdate = {
  language: Language;
};

export namespace WriterState {
  const values = ["APPLICATION", "PROBATION", "REGULAR", "VETERAN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type WriterState = WriterState.Impl;

export namespace XApiActorId {
  const values = ["MBOX", "ACCOUNT", "OPENID"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type XApiActorId = XApiActorId.Impl;

export type XApiStatement = {
  actor: Actor;
  object: Activity;
  result: Result;
  timestamp: string;
  verb: Verb;
};

export type ZipTown = {
  town: string;
};

export type ZonedDateTime = {
  datetime: string;
  timezone: string;
};
